// Диалог с аватаром (аудит, этап 2 — декомпозиция): playback-движок вынесен в
// avg-dialog-playback.ts (единая очередь видео/TTS, double-buffer, idle);
// здесь остаются конфиг/сессия, разговор + SSE-поток, live-сессия аватара,
// голосовой ввод и жизненный цикл вью.
import './avg-dialog.css';
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import { renderMarkdown } from '../shared/ui';
import { fetchAvatarProfiles, fillAvatarProfileSelect, saveAvatarProfile } from '../shared/avatar-profile';
import { showRegistrationPrompt, switchView } from '../shared/navigation';
import { openConversationSession } from '../shared/app-hooks';
import {
  isHeyGenAvatarActive,
  speakHeyGenAvatar,
  startHeyGenAvatar,
  stopHeyGenAvatar,
} from './avg-dialog-heygen';
import {
  configureAvatarPlayback,
  enqueueAvatarVideo,
  enqueueTtsAudio,
  getAvatarVideo,
  isAvatarSpeaking,
  primePlaybackWithGesture,
  setAvatarVideo,
  setHqIndicator,
  stopLivePlayback,
} from './avg-dialog-playback';
import {
  type CaptionsCtx,
  type CaptionsSideEffects,
  createCaptionsCtx,
  fallbackPlaybackEnded,
  finishCaptionsStream,
  flushCaptionsOnInterrupt,
  gatedPlaybackEnded,
  holdUnitText,
} from './avg-dialog-captions';

/** Режим текстовой фиксации ответа — см. серверный AvgDialogCaptionsMode. */
export type AvgDialogCaptionsMode = 'immediate' | 'after_playback' | 'off';

export type AvgDialogCategoryConfig = {
  name: string;
  avatar_id: string;
  avatar_label: string;
  idle_video_url?: string;
  face_image_url?: string;
  /** Заставка — статичная подложка окна видео (видна, пока нет кадров). */
  splash_url?: string;
  greeting: string;
  heygen_avatar_name?: string;
  heygen_voice_id?: string;
  // wav2lip_live (local avatar) fields — optional, provided by /api/avg-dialog/config
  idle_animation?: string;
  video_resolution?: '480p' | '720p';
  avatar_voice?: string;
  /** Движок липсинка профиля: kling рендерит в облаке минутами. */
  lipsync_engine?: 'wav2lip' | 'kling';
  /** video_first (дефолт) — звук хода придерживается и играет ОДНИМ видео на
   * весь ход; audio_first — прежний стриминг клипов-«абзацев». */
  local_video_policy?: 'video_first' | 'audio_first';
  /** Эффективный (профиль > глобальная настройка > env) режим показа текста. */
  captions_mode?: AvgDialogCaptionsMode;
  /** Эффективный (профиль > глобальная настройка > env) флаг перебивания. */
  barge_in_enabled?: boolean;
  /** Голосовое перебивание (VAD): эффективно только вместе с barge_in_enabled. */
  voice_barge_in_enabled?: boolean;
};

type AvgDialogConfig = {
  enabled: boolean;
  mode: 'scripted' | 'live';
  live_provider: string;
  stt_provider: string;
  barge_in_enabled: boolean;
  voice_barge_in_enabled: boolean;
  max_turns: number;
  timeout_min: number;
  avatar_error?: string;
  categories: AvgDialogCategoryConfig[];
};

type ConversationMessage = {
  role: 'user' | 'assistant';
  text: string;
};

type SsePayload = {
  type?: string;
  text?: string;
  tts?: { chunk_index: number; text: string; audio_url?: string };
  turn_count?: number;
  video_url?: string;
  error?: string;
};

// Bookkeeping for one live SSE turn — accumulates the streamed answer text so
// it can be shown as a growing assistant bubble in the conversation.
// Показ текста режима captions_mode=after_playback (heldText/revealedParts/
// finalText/streamDone/committed/pendingPlaybackItems) — чистая логика в
// avg-dialog-captions.ts (юнит-тестируется без DOM); здесь только поля,
// специфичные для остального хода (голос, пер-ходовое видео).
interface LiveTurnCtx extends CaptionsCtx {
  answerParts: string[];
  /** video_first: голос юнитов придерживается здесь до пер-ходового видео;
   * если ход завершился без видео — проигрывается как голосовой фолбэк. */
  heldAudio: string[];
  /** Пер-ходовое видео уже пришло и играет — придержанный голос не нужен. */
  turnVideoPlayed: boolean;
}

/** Ход, чей текст ещё может быть не закоммичен (captions_mode=after_playback) —
 * нужен «стоп»/barge-in, чтобы не потерять придержанный текст прерванного ответа. */
let activeTurnCtx: LiveTurnCtx | null = null;

const SESSION_KEY = 'avg_dialog_session_id';

let config: AvgDialogConfig | null = null;
let activeCategory = '';
let messages: ConversationMessage[] = [];
/** Сколько закоммиченных реплик уже висит в ленте отдельными узлами
 * (инкрементальный рендер, см. renderMessages). -1 — в ленте не история
 * (приветствие/пусто), состав надо пересобрать целиком. */
let renderedMessages = -1;
let turnCount = 0;
/** Сколько ходов уже перенесено в Разговор — по нему видно, есть ли
 * несохранённое (кнопка «Новый диалог» тогда переспрашивает). */
let savedTurnCount = 0;
let isSavingChat = false;
let maxTurns = 30;
let isSending = false;
let eventsBound = false;
let liveRecognition: LiveSpeechRecognition | null = null;
let currentStreamAbort: AbortController | null = null;
/** Вопрос хода, по которому ещё НИЧЕГО не пришло (аватар молчит, текста нет).
 * «Стоп» в этом окне возвращает вопрос в поле ввода — ход отменяется целиком,
 * а не обрывается на полуслове (см. interruptAvgDialog). Обнуляется первым же
 * чанком ответа: дальше «Стоп» работает по-старому — прерывает ответ. */
let pendingQuestionText: string | null = null;

interface SpeechRecognitionResultList {
  [index: number]: { isFinal?: boolean; [index: number]: { transcript: string } };
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface LiveSpeechRecognition extends EventTarget {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onstart: (() => void) | null;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing && /^[0-9a-f-]{36}$/i.test(existing)) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

function resetSession(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
  messages = [];
  renderedMessages = -1;
  turnCount = 0;
  savedTurnCount = 0;
  activeTurnCtx = null;
  pendingQuestionText = null;
  stopLivePlayback();
  resetVoiceVadState();
  currentStreamAbort?.abort();
  currentStreamAbort = null;
}

function isLiveMode(): boolean {
  return config?.mode === 'live';
}

function getActiveCategoryConfig(): AvgDialogCategoryConfig | null {
  if (!config?.categories?.length) return null;
  return config.categories.find((c) => c.name === activeCategory) || config.categories[0] || null;
}

function setLiveStatus(text: string): void {
  const el = $('adlg-live-status');
  if (el) el.textContent = text;
}

function idleVideoUrl(): string | undefined {
  const cat = getActiveCategoryConfig();
  return cat?.idle_video_url || cat?.idle_animation;
}

function currentFaceUrl(): string | undefined {
  return getActiveCategoryConfig()?.face_image_url;
}

function currentSplashUrl(): string | undefined {
  return getActiveCategoryConfig()?.splash_url;
}

// Источники idle/face/splash-медиа для playback-движка — late-bound, на module evaluation.
configureAvatarPlayback({
  idleVideoUrl,
  faceUrl: currentFaceUrl,
  splashUrl: currentSplashUrl,
});

function isCloudLipsync(): boolean {
  return getActiveCategoryConfig()?.lipsync_engine === 'kling';
}

/**
 * Пер-ходовая технология (video_first, дефолт профиля): звук хода не играет по
 * мере прихода tts_chunk — он придерживается до единственного видео на весь ход
 * (безупречная картинка без склеек; первый кадр ~8–10 с, «Заставка» закрывает
 * ожидание). Не действует для облачного липсинка (Kling рендерит минуты — голос
 * стримится, HQ-видео догоняет) и для HeyGen (свой стриминговый канал).
 */
function holdsAudioForTurnVideo(): boolean {
  const cat = getActiveCategoryConfig();
  if (!cat || cat.lipsync_engine === 'kling') return false;
  // Видеоответы выключены пользователем — ждать нечего: сервер не запускает
  // рендер вовсе (users.dialog_video_enabled, см. live-proxy). Голос играет по
  // мере прихода, сцену держат заставка и idle-клипы.
  if (!videoAnswersEnabled()) return false;
  return (cat.local_video_policy || 'video_first') !== 'audio_first';
}

/** Опция пользователя «видеоответы в Диалоге»; по умолчанию включена. */
function videoAnswersEnabled(): boolean {
  return state.currentUser?.dialog_video_enabled !== false;
}

/** Режим текстовой фиксации ответа профиля (см. AvgDialogCaptionsMode). */
function captionsMode(): AvgDialogCaptionsMode {
  return getActiveCategoryConfig()?.captions_mode || 'immediate';
}

/** The dialog profile (avatar) name — shown as a read-only line when the user
 * may not switch profiles (the selector replaces it otherwise). */
function avatarProfileLabel(): string {
  return getActiveCategoryConfig()?.avatar_label || 'Аватар';
}

/**
 * Селектор профиля аватара под видео — то же поле, что в настройках и в
 * админке. Платные профили без кредитов приходят выключенными, а не срезанными:
 * пусть видно, что премиум есть. Надпись с именем остаётся запасным вариантом
 * на случай, когда профилей нет вовсе.
 */
async function renderAvatarProfileSelector(): Promise<void> {
  const select = $<HTMLSelectElement>('adlg-avatar-profile');
  const nameEl = $('adlg-avatar-profile-name');
  if (!select) return;

  const profiles = await fetchAvatarProfiles();
  const selectable = profiles.length > 0;

  select.classList.toggle('hidden', !selectable);
  nameEl?.classList.toggle('hidden', selectable);

  if (!selectable) {
    if (nameEl) nameEl.textContent = avatarProfileLabel();
    return;
  }

  // Текущее значение ищем среди ДОСТУПНЫХ профилей. Раньше сюда шёл
  // эффективный avatar_id из конфига — а он подставляет глобальный профиль по
  // умолчанию, когда пользователю ничего не назначено, и такой профиль лез в
  // список отдельной строкой мимо набора «Доступные профили Диалога».
  const assigned = (state.currentUser?.dialog_avatar_id as string | null) || '';
  const effective = getActiveCategoryConfig()?.avatar_id || '';
  const current = [assigned, effective].find((id) => id && profiles.some((p) => p.avatar_id === id))
    || profiles[0].avatar_id;

  fillAvatarProfileSelect(select, profiles, current);
}

/** Смена профиля: сохраняем выбор и перезапускаем Диалог — новый профиль может
 * принести другую категорию, то есть другую модель и базу знаний. */
async function onAvatarProfileChange(): Promise<void> {
  const select = $<HTMLSelectElement>('adlg-avatar-profile');
  if (!select) return;
  // Откатываем на назначенный профиль, а не на эффективный: последний может
  // быть глобальным дефолтом, которого в списке нет.
  const previous = (state.currentUser?.dialog_avatar_id as string | null)
    || getActiveCategoryConfig()?.avatar_id || '';
  const next = select.value || '';
  if (next === previous) return;

  select.disabled = true;
  try {
    const result = await saveAvatarProfile(next);
    if (!result.ok) {
      select.value = previous;
      showToast('❌ ' + result.error);
      // Free-аккаунт выбрал платного аватара: ведём на регистрацию.
      if (result.code === 'registration_required') showRegistrationPrompt();
      return;
    }
    if (state.currentUser) state.currentUser.dialog_avatar_id = next || null;
    await endLiveAvatarSession();
    resetSession();
    await loadAvgDialogView();
    // Тоста об успехе нет намеренно: аватар в окне уже сменился на глазах —
    // подтверждать это надписью нечего. Ошибка переключения по-прежнему
    // показывается (её из UI не видно).
  } finally {
    select.disabled = false;
  }
}

async function startLiveAvatarSession(): Promise<void> {
  if (!isLiveMode()) return;

  const video = getAvatarVideo();
  if (!video) return;

  setLiveStatus('Подключение к аватару…');

  // Голос инициализируется параллельно подключению аватара: к первому клику по
  // кнопке устройство готово, способ распознавания известен, а неготовность
  // видна на самой кнопке (см. initVoiceReadiness). Намеренно без await —
  // отказ в доступе не должен ломать старт сессии.
  void initVoiceReadiness();
  // Голосовое перебивание — включаемая настройка (см. maybeStartVoiceVadMonitor),
  // не зависит от результата фетча ниже.
  maybeStartVoiceVadMonitor();

  try {
    const r = await fetch('/api/avg-dialog/live/session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + state.authToken,
      },
      body: JSON.stringify({
        session_id: getSessionId(),
        category: activeCategory,
      }),
    });

    if (!r.ok) throw new Error('Не удалось открыть live-сессию');
    const data = await r.json() as {
      live_provider: string;
      access_token?: string;
      status: string;
    };

    if (data.live_provider === 'heygen' && data.access_token) {
      const ok = await startHeyGenAvatar({
        accessToken: data.access_token,
        videoElement: video,
        onReady: () => setLiveStatus(''),
        onError: (msg) => {
          setLiveStatus('HeyGen: ' + msg);
          setAvatarVideo(idleVideoUrl());
        },
      });
      if (!ok) setAvatarVideo(idleVideoUrl());
    } else {
      // Имя профиля теперь живёт в селекторе под видео, поэтому строка статуса
      // при успешном подключении молчит: транспорт пользователю знать незачем.
      setLiveStatus('');
      setAvatarVideo(idleVideoUrl());
    }
  } catch (err) {
    setLiveStatus('Live недоступен');
    setAvatarVideo(idleVideoUrl());
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

async function endLiveAvatarSession(): Promise<void> {
  stopLivePlayback();
  stopVoiceVadMonitor();
  await stopHeyGenAvatar();
  const sessionId = getSessionId();
  try {
    await fetch(`/api/avg-dialog/live/session/${encodeURIComponent(sessionId)}`, {
      method: 'DELETE',
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
  } catch {
    // ignore
  }
}

function updateAvatarPanel(): void {
  // Тумблер микрофона и Свободный диалог — только для живого режима
  // (видимость тумблера держит updateMicToggle: она же знает про правку).
  updateMicToggle();
  updatePrimaryAction();
  updateSaveChatButton();
  // Высота поля всегда идёт от содержимого — и при входе в раздел тоже,
  // иначе стартовые rows="2" разъезжались бы с высотой после первой отправки.
  autoResizeDialogInput();
  // Колонка аватара обнимает кадр 9:16 — пересчёт при каждом обновлении панели
  // (ResizeObserver в фоновых вкладках заморожен, синхронный замер — нет).
  syncStageColumnWidth();

  // Заставка профиля — подложка сцены: показана всегда, пока профиль её задаёт;
  // видеослои с кадрами закрывают её сверху (см. слои в avg-dialog.css).
  const splash = $('adlg-avatar-splash') as HTMLImageElement | null;
  const splashUrl = getActiveCategoryConfig()?.splash_url || '';
  if (splash) {
    if (splashUrl) {
      if (splash.getAttribute('src') !== splashUrl) splash.src = splashUrl;
      splash.classList.remove('hidden');
    } else {
      splash.classList.add('hidden');
      splash.removeAttribute('src');
    }
  }
}

/** Builds a conversation bubble whose text is rendered through the shared Markdown
 * pipeline (same standard as the main Conversation: headings, lists, code, links). */
function buildBubble(role: ConversationMessage['role'], text: string, streaming = false): HTMLElement {
  const cls = role === 'user' ? 'adlg-msg-user' : 'adlg-msg-assistant';
  const bubble = document.createElement('div');
  bubble.className = `adlg-msg ${cls}${streaming ? ' adlg-msg-streaming' : ''}`;
  const content = document.createElement('div');
  content.className = 'adlg-msg-content';
  renderMarkdown(content, text);
  bubble.appendChild(content);
  // Копия реплики одной кнопкой: выделять мышью в живом ходе неудобно (текст
  // ответа доезжает частями), а голосовой разговор как раз тем и ценен, что
  // ответ хочется унести к себе целиком. Растущий бабл кнопку не получает —
  // копировать недописанное нечего.
  if (!streaming) bubble.appendChild(buildCopyButton(text));
  return bubble;
}

/** Кнопка «Копировать» реплики: сам текст, а не разметка бабла. */
function buildCopyButton(text: string): HTMLElement {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'adlg-msg-copy';
  btn.title = 'Копировать реплику';
  btn.setAttribute('aria-label', 'Копировать реплику');
  btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" '
    + 'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
    + '<rect x="9" y="9" width="13" height="13" rx="2"/>'
    + '<path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
  btn.addEventListener('click', () => {
    void copyMessageText(text, btn);
  });
  return btn;
}

async function copyMessageText(text: string, btn: HTMLElement): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // Буфер обмена закрыт политикой браузера (не-https, отказ в доступе) —
    // старый путь через скрытое поле работает и там.
    const area = document.createElement('textarea');
    area.value = text;
    area.setAttribute('readonly', '');
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    try { document.execCommand('copy'); } catch { /* совсем нельзя — молчим */ }
    area.remove();
  }
  btn.classList.add('adlg-msg-copied');
  setTimeout(() => btn.classList.remove('adlg-msg-copied'), 1200);
}

/** Renders the conversation. When `streaming` is provided, appends a live assistant
 * bubble with the partial answer (or a "Думаю…" placeholder while empty). */
function renderMessages(streaming?: string | null): void {
  const host = $('adlg-messages');
  if (!host) return;

  const showStreaming = streaming !== undefined && streaming !== null;
  if (!messages.length && !showStreaming) {
    // Пустой разговор встречает «Приветствием» из настроек профиля аватара.
    const greeting = getActiveCategoryConfig()?.greeting
      || 'Задайте первый вопрос — ответ появится в разговоре.';
    host.innerHTML = `<div class="adlg-empty adlg-greeting">${escapeHtml(greeting)}</div>`;
    renderedMessages = -1;
    return;
  }

  // Человек, отлиставший вверх, остаётся там: раньше каждый кадр стрима
  // возвращал ленту в самый низ, и перечитать ответ по ходу разговора было
  // нельзя.
  const atBottom = host.scrollHeight - host.scrollTop - host.clientHeight < 40;

  // Инкрементальный рендер. Полная пересборка на каждом кадре стрима сбрасывала
  // выделение мышью — скопировать реплику было физически невозможно, пока идёт
  // ход. Закоммиченные баблы теперь остаются теми же узлами, меняется только
  // растущий последний.
  host.querySelector('.adlg-msg-streaming')?.remove();
  if (renderedMessages < 0
    || renderedMessages > messages.length
    || host.children.length !== renderedMessages) {
    host.innerHTML = '';
    renderedMessages = 0;
  }
  for (let i = renderedMessages; i < messages.length; i++) {
    host.appendChild(buildBubble(messages[i].role, messages[i].text));
  }
  renderedMessages = messages.length;

  if (showStreaming) {
    if (streaming) {
      host.appendChild(buildBubble('assistant', streaming, true));
    } else {
      const bubble = document.createElement('div');
      bubble.className = 'adlg-msg adlg-msg-assistant adlg-msg-streaming';
      bubble.innerHTML = '<span class="adlg-typing">Думаю…</span>';
      host.appendChild(bubble);
    }
  }
  if (atBottom) host.scrollTop = host.scrollHeight;
}

function updateTurnHint(): void {
  updateSaveChatButton();
  const hint = $('adlg-turn-hint');
  if (!hint) return;
  const remaining = Math.max(0, maxTurns - turnCount);
  // Исчерпанный лимит беседу не уничтожает (сессия жива, транскрипт на сервере
  // тоже) — это как раз момент сохранить её в Разговор, о чём и напоминаем.
  hint.textContent = remaining > 0
    ? `Осталось реплик: ${remaining} из ${maxTurns}`
    : 'Лимит реплик исчерпан — сохраните чат в Разговор или начните новый диалог.';
}

async function parseSseStream(
  response: Response,
  handlers: {
    onChunk: (payload: SsePayload) => void;
    onDone: (payload: SsePayload) => void;
    onError?: (message: string) => void;
  }
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) throw new Error('Поток недоступен');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';

    for (const part of parts) {
      const line = part.split('\n').find((l) => l.startsWith('data: '));
      if (!line) continue;
      const raw = line.slice(6).trim();
      if (raw === '[DONE]') return;
      try {
        const payload = JSON.parse(raw) as SsePayload;
        if (payload.type === 'error') {
          handlers.onError?.(payload.error || payload.text || 'Ошибка генерации ответа');
          return;
        }
        if (payload.type === 'done') handlers.onDone(payload);
        else handlers.onChunk(payload);
      } catch {
        // ignore malformed chunk
      }
    }
  }
}

/**
 * Ход закоммичен в историю — сообщаем страницу об этом наружу.
 *
 * Диалог не знает, кто его открыл: событие — единственный канал для обёрток
 * (Mini App Telegram зеркалит разговор в чат, см. telegram-app.js в
 * messenger-gateway). Вопрос берём из истории: он уже лежит последним
 * user-сообщением к моменту любого коммита ответа.
 */
function announceTurnCommitted(answer: string): void {
  if (!answer) return;
  const question = [...messages].reverse().find((m) => m.role === 'user')?.text || '';
  document.dispatchEvent(
    new CustomEvent('avg-dialog:turn-committed', { detail: { question, answer } }),
  );
}

/** Прокладывает побочные эффекты (рендер бабла/коммит в историю/спикинг-флаг)
 * из avg-dialog.ts в чистую логику avg-dialog-captions.ts. */
function captionsFx(ctx: LiveTurnCtx): CaptionsSideEffects {
  return {
    renderLive: (text) => renderMessages(text),
    commit: (text) => {
      if (text) {
        messages.push({ role: 'assistant', text });
        announceTurnCommitted(text);
      }
      renderMessages();
      if (activeTurnCtx === ctx) activeTurnCtx = null;
    },
    isAvatarSpeaking,
  };
}

/**
 * Показ текста ОДНОГО юнита хода — маршрутизирует по captions_mode:
 *   immediate       — сразу в растущий «живой» бабл (прежнее поведение);
 *   after_playback  — придерживаем; покажется, когда доиграет ЕГО
 *                      аудио/видео (см. playbackCallbacks/avg-dialog-captions.ts);
 *   off             — только копится в answerParts (fallback для finalText),
 *                      в UI не попадает.
 * answerParts копится всегда — им пользуется и SSE 'done' (fallback finalText),
 * и голосовая озвучка (HeyGen), независимо от режима показа.
 */
function presentUnitText(ctx: LiveTurnCtx, text: string): void {
  ctx.answerParts.push(text);
  if (ctx.captionsMode === 'immediate') {
    renderMessages(ctx.answerParts.join(' '));
  } else {
    holdUnitText(ctx, text); // no-op вне after_playback
  }
}

/** captions_mode=after_playback: колбэк onEnded ОСНОВНОГО элемента очереди —
 * пер-ходовое видео (video_first) либо юнит-клип/аудио (audio_first). Держит
 * ctx.pendingPlaybackItems > 0, пока клип не доиграет — см.
 * avg-dialog-captions.ts:gatedPlaybackEnded/finishCaptionsStream. undefined
 * в остальных режимах — плейбеку синхронизировать нечего. */
function playbackCallbacks(ctx: LiveTurnCtx): { onEnded?: () => void } | undefined {
  const onEnded = gatedPlaybackEnded(ctx, captionsFx(ctx));
  return onEnded ? { onEnded } : undefined;
}

/** captions_mode=after_playback: колбэк onEnded для голосового ФОЛБЭКА
 * (flushHeldAudio ниже — пер-ходовое видео не пришло). В отличие от
 * playbackCallbacks НЕ учитывается в pendingPlaybackItems: деградация
 * раскрывает текст сразу по 'done' (см. fallbackPlaybackEnded), а этот
 * колбэк лишь синхронизирует финальный коммит с концом фолбэк-озвучки. */
function fallbackPlaybackCallbacks(ctx: LiveTurnCtx): { onEnded?: () => void } | undefined {
  const onEnded = fallbackPlaybackEnded(ctx, captionsFx(ctx));
  return onEnded ? { onEnded } : undefined;
}

/**
 * Barge-in/«стоп»/обрыв стрима во время captions_mode=after_playback:
 * придержанный текст не должен потеряться — коммитим то, что успело прийти,
 * немедленно, вместо того чтобы ждать доигрывания (которое мы сами же
 * обрываем stopLivePlayback). immediate/off тут ни при чём: immediate уже
 * показан, off никогда не покажется.
 */
function flushTurnTextOnInterrupt(ctx: LiveTurnCtx | null): void {
  if (!ctx) return;
  flushCaptionsOnInterrupt(ctx, captionsFx(ctx), ctx.answerParts.join(' '));
}

function handleLiveChunk(payload: SsePayload, ctx: LiveTurnCtx): void {
  // Ответ пошёл — вопрос больше не отменяемый: «Стоп» теперь прерывает ответ,
  // а не возвращает вопрос в поле ввода.
  pendingQuestionText = null;
  if (payload.type === 'avatar' && payload.video_url) {
    setHqIndicator(false);
    if (payload.tts?.text) {
      // Стриминговый клип-«абзац» (audio_first; muxed audio). Add its text to the
      // conversation and queue the clip; the clip's own audio is the voice — no
      // separate TTS.
      presentUnitText(ctx, payload.tts.text);
      if (isHeyGenAvatarActive()) {
        speakHeyGenAvatar(payload.tts.text);
      } else {
        enqueueAvatarVideo(payload.video_url, playbackCallbacks(ctx));
      }
    } else if (!isHeyGenAvatarActive()) {
      // Пер-ходовое видео (video_first / premium finalize): единственный клип
      // с голосом ВСЕГО хода. Придержанный голос юнитов больше не нужен —
      // играет только видео, без единой склейки. Показ придержанного текста
      // (after_playback) синхронизирован с ЭТИМ видео — оно несёт все юниты.
      ctx.turnVideoPlayed = true;
      ctx.heldAudio = [];
      stopLivePlayback();
      enqueueAvatarVideo(payload.video_url, playbackCallbacks(ctx));
    }
  } else if (payload.type === 'tts_chunk' && payload.tts?.text) {
    // Юнит хода: текст — по режиму captions_mode; голос — по политике профиля.
    const sentence = payload.tts.text;
    presentUnitText(ctx, sentence);
    if (isHeyGenAvatarActive()) {
      speakHeyGenAvatar(sentence);
    } else if (payload.tts.audio_url) {
      if (holdsAudioForTurnVideo()) {
        // video_first: звук ждёт полное видео хода — придерживаем и показываем,
        // что видео готовится (сцену закрывает «Заставка»/idle-петля).
        ctx.heldAudio.push(payload.tts.audio_url);
        setHqIndicator(true);
      } else {
        enqueueTtsAudio(payload.tts.audio_url, playbackCallbacks(ctx));
      }
    }
    // Premium: the HQ (Kling) render trails the streamed audio — show a hint.
    if (isCloudLipsync() && !isHeyGenAvatarActive()) {
      setHqIndicator(true);
    }
  } else if (payload.type === 'delta' && payload.text) {
    // Фолбэк без озвучки (текстовый ход): юнита воспроизведения нет —
    // синхронизировать captions_mode не с чем, показываем как immediate.
    ctx.answerParts.push(payload.text);
    renderMessages(ctx.answerParts.join(' '));
  }
}

/** Ход завершился без пер-ходового видео (таймаут/сбой рендера): придержанный
 * голос юнитов проигрывается как фолбэк — ответ не остаётся немым. */
function flushHeldAudio(ctx: LiveTurnCtx): void {
  if (ctx.turnVideoPlayed || !ctx.heldAudio.length) return;
  for (const url of ctx.heldAudio) enqueueTtsAudio(url, fallbackPlaybackCallbacks(ctx));
  ctx.heldAudio = [];
}

async function sendQuestion(text: string, options: { bargeIn?: boolean } = {}): Promise<void> {
  const trimmed = text.trim();
  if (!trimmed || isSending || !activeCategory) return;
  if (turnCount >= maxTurns) {
    showToast('Лимит реплик исчерпан. Начните новый диалог.', 'error');
    return;
  }

  if (options.bargeIn) {
    // Барж-ин уже учёл config.barge_in_enabled на стороне вызывающего (см.
    // bindEvents) — здесь только локальный стоп + сохранение придержанного
    // текста прерванного хода (captions_mode=after_playback).
    flushTurnTextOnInterrupt(activeTurnCtx);
    stopLivePlayback();
  }

  // Ход ушёл — отложенная авто-отправка своё отработала: не дать ей стрельнуть
  // вторым разом по уже отправленному тексту.
  cancelFreeAutoSend();
  noteFreeDialogActivity();
  // ВРЕМЕННОЕ (этап отладки голоса): что именно ушло вопросом. Без этого по
  // журналу видно только «сколько символов распозналось», а сбой логики
  // вопрос-ответ различается лишь по тексту — на телефоне консоли нет.
  voiceLog('ход отправлен', { chars: trimmed.length, text: trimmed.slice(0, 200) });
  reportVoiceTrace('turn-sent');

  const input = $('adlg-input') as HTMLTextAreaElement | null;
  isSending = true;
  const sendBtn = $('adlg-send-btn') as HTMLButtonElement | null;
  const freeBtn = $('adlg-free-btn') as HTMLButtonElement | null;
  if (sendBtn) sendBtn.disabled = true;
  if (freeBtn) freeBtn.disabled = true;

  messages.push({ role: 'user', text: trimmed });
  // Пока ход не принёс ни звука, ни текста, вопрос считается отменяемым:
  // «Стоп» вернёт его в поле ввода вместо того, чтобы оставить висеть.
  pendingQuestionText = trimmed;
  renderMessages(''); // user message + "Думаю…" assistant placeholder
  if (input) input.value = '';
  autoResizeDialogInput();
  // Правленый текст ушёл ходом — голос возвращается в состояние до правки:
  // Диалог снова включён или выключен ровно так, как был.
  endTextEditing();
  // Поле опустело — главное действие перестаёт быть «Отправить».
  updatePrimaryAction();

  const useStream = isLiveMode();
  currentStreamAbort = new AbortController();

  try {
    const r = await fetch('/api/avg-dialog/turn', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + state.authToken,
      },
      body: JSON.stringify({
        session_id: getSessionId(),
        category: activeCategory,
        user_text: trimmed,
        stream: useStream,
        barge_in: !!options.bargeIn,
      }),
      signal: currentStreamAbort.signal,
    });

    if (!r.ok) {
      const err = await r.json().catch(() => ({})) as { error?: { message?: string } };
      throw new Error(err.error?.message || 'Не удалось получить ответ');
    }

    if (useStream && r.headers.get('content-type')?.includes('text/event-stream')) {
      const ctx: LiveTurnCtx = {
        ...createCaptionsCtx(captionsMode()),
        answerParts: [],
        heldAudio: [],
        turnVideoPlayed: false,
      };
      activeTurnCtx = ctx;
      let finalText = '';
      let finalTurnCount = turnCount;

      try {
        await parseSseStream(r, {
          onChunk: (payload) => handleLiveChunk(payload, ctx),
          onDone: (payload) => {
            finalText = payload.text || ctx.answerParts.join(' ') || '';
            finalTurnCount = payload.turn_count ?? turnCount + 1;
            // The server decided about the turn video before `done` — drop the
            // hint and, if the video never arrived, voice the held units.
            setHqIndicator(false);
            flushHeldAudio(ctx);
          },
          onError: (message) => {
            setHqIndicator(false);
            stopLivePlayback();
            activeTurnCtx = null;
            renderMessages();
            showToast('❌ ' + message, 'error');
          },
        });
      } finally {
        // Обрыв стрима до `done` не должен оставить ход немым: гасим индикатор
        // и озвучиваем придержанные юниты (flush идемпотентен).
        setHqIndicator(false);
        flushHeldAudio(ctx);
      }

      turnCount = finalTurnCount;
      if (ctx.captionsMode === 'off') {
        // Текст не показываем — ответ остаётся в истории сессии на сервере;
        // локальный «Думаю…»-бабл снимаем.
        renderMessages();
        activeTurnCtx = null;
      } else if (ctx.captionsMode === 'after_playback') {
        // Раскрытие (и коммит в историю) синхронизировано с воспроизведением
        // через revealHeldText/gatedPlaybackEnded (onEnded очереди плейбека,
        // см. avg-dialog-captions.ts). finishCaptionsStream раскрывает
        // немедленно ТОЛЬКО если играть уже нечего (pendingPlaybackItems===0:
        // voice_only не пришёл, текстовый фолбэк, ошибка) — иначе ждёт onEnded
        // доигрывающего видео/аудио хода, а не раскрывает текст на его старте.
        finishCaptionsStream(ctx, captionsFx(ctx), finalText);
      } else {
        if (finalText) {
          messages.push({ role: 'assistant', text: finalText });
          announceTurnCommitted(finalText);
        }
        renderMessages();
        if (isHeyGenAvatarActive() && ctx.answerParts.length === 0) {
          speakHeyGenAvatar(finalText);
        }
        activeTurnCtx = null;
      }
    } else {
      const data = await r.json() as {
        answer_text: string;
        turn_count: number;
        turn_cap_reached: boolean;
        dialog_timeout: boolean;
        tts_chunks?: Array<{ text: string; audio_url?: string }>;
        avatar: { video_url?: string; audio_url?: string };
      };

      turnCount = data.turn_count ?? turnCount + 1;
      const answer = data.answer_text || '';
      messages.push({ role: 'assistant', text: answer });
      announceTurnCommitted(answer);
      renderMessages();

      if (isHeyGenAvatarActive()) {
        speakHeyGenAvatar(answer);
      } else if (data.avatar.video_url && holdsAudioForTurnVideo()) {
        // Пер-ходовое видео несёт голос всего хода — озвучка юнитов лишняя.
        enqueueAvatarVideo(data.avatar.video_url);
      } else {
        for (const chunk of data.tts_chunks || []) {
          if (chunk.audio_url) enqueueTtsAudio(chunk.audio_url);
        }
        if (!data.tts_chunks?.length && data.avatar.audio_url) {
          enqueueTtsAudio(data.avatar.audio_url);
        }
        if (data.avatar.video_url) enqueueAvatarVideo(data.avatar.video_url);
      }

      if (data.dialog_timeout || data.turn_cap_reached) {
        await restartDialog();
      }
    }

    updateTurnHint();
  } catch (err) {
    // Обрыв (сеть/abort) не должен молча похоронить придержанный текст
    // (captions_mode=after_playback) — коммитим то, что успело прийти.
    flushTurnTextOnInterrupt(activeTurnCtx);
    activeTurnCtx = null;
    if ((err as Error).name === 'AbortError') return;
    renderMessages();
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)), 'error');
  } finally {
    isSending = false;
    pendingQuestionText = null;
    if (sendBtn) sendBtn.disabled = false;
    if (freeBtn) freeBtn.disabled = false;
    currentStreamAbort = null;
    updatePrimaryAction();
  }
}

async function restartDialog(): Promise<void> {
  await endLiveAvatarSession();
  resetSession();
  getSessionId();
  await startLiveAvatarSession();
  updateAvatarPanel();
  renderMessages();
  updateTurnHint();
}

function hasUnsavedTurns(): boolean {
  return turnCount > savedTurnCount;
}

/**
 * Доступность «Сохранить в Разговор» считается по числу ходов, а НЕ по длине
 * локального `messages`: при captions_mode=off тексты ответов клиенту не
 * показываются и в массив не попадают, хотя беседа идёт (на сервере она
 * записана целиком — см. avg-dialog.transcript).
 */
function updateSaveChatButton(): void {
  const btn = $<HTMLButtonElement>('adlg-save-chat');
  if (!btn) return;
  btn.disabled = isSavingChat || turnCount === 0;
}

/**
 * Переносит беседу в список чатов Разговора и открывает её там. Уход с вкладки
 * закрывает live-сессию аватара (switchView → teardownAvgDialog) — к этому
 * моменту чат уже записан в БД, а серверный транскрипт переживает закрытие
 * сессии, так что вернувшись, можно дописать новые ходы в тот же чат.
 */
async function saveChatToConversation(): Promise<void> {
  if (isSavingChat || turnCount === 0) return;
  isSavingChat = true;
  updateSaveChatButton();

  try {
    const r = await fetch('/api/avg-dialog/chat/save', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + state.authToken,
      },
      body: JSON.stringify({ session_id: getSessionId() }),
    });

    if (!r.ok) {
      const data = await r.json().catch(() => ({})) as {
        detail?: string;
        error?: { message?: string; code?: string };
      };
      // Free-аккаунт: своей истории чатов у него нет — ведём на регистрацию.
      if (data.error?.code === 'registration_required') {
        showToast('❌ ' + (data.error.message || 'Требуется регистрация'), 'error');
        showRegistrationPrompt();
        return;
      }
      throw new Error(data.error?.message || data.detail || 'Не удалось сохранить чат');
    }

    const saved = await r.json() as { session_id: string };
    savedTurnCount = turnCount;
    showToast('💬 Чат сохранён в Разговор');

    await switchView('conversation');
    const opened = await openConversationSession(saved.session_id);
    if (!opened) showToast('Откройте чат в списке разговоров', 'info');
  } catch (err) {
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)), 'error');
  } finally {
    isSavingChat = false;
    updateSaveChatButton();
  }
}

type MicState = 'idle' | 'preparing' | 'listening';

/**
 * Свободный диалог: речь пишется в поле ввода прямо по ходу фразы, по её концу
 * реплика уходит аватару сама (после грейс-окна, см. scheduleFreeAutoSend), а
 * когда аватар договорит — микрофон включается снова. Кнопка режима — простой
 * Старт/Стоп, как и была. Пауза диктовки — тумблер микрофона: клик глушит
 * запись и оставляет текст в поле на правку, повторный клик продолжает
 * диктовку с дописыванием. Вне режима поведение прежнее: голос падает в поле
 * ввода, пользователь проверяет текст и отправляет сам.
 */
let freeDialogEnabled = false;
/** Отложенная авто-отправка Свободного диалога (см. scheduleFreeAutoSend). */
let freeAutoSendTimer: ReturnType<typeof setTimeout> | null = null;
/**
 * Грейс-окно между концом фразы и авто-отправкой. Раньше реплика улетала в тот
 * же миг, что распозналась, — остановиться «сформулировать конец вопроса» было
 * физически невозможно. Теперь конец фразы лишь взводит таймер: продолжение
 * речи или «Пауза» его снимают, и только непрерванное окно отправляет ход.
 *
 * Было три секунды, потом пять — «подбираю слово» человек делает длиннее, чем
 * кажется. Возврат к трём (решение владельца от 06.08.2026, стенд): продолжение
 * речи снимает окно надёжно с тех пор, как перевзведённая запись перестала
 * уезжать в распознавание вслед за ушедшим ходом, и пять секунд стали просто
 * ожиданием — к ним добавляется ещё секунда-две распознавания.
 */
const FREE_AUTOSEND_GRACE_MS = 3000;
/** Микрофон выключен пользователем: глушит и распознавание, и VAD. */
let micMuted = false;
/**
 * Человек набрал текст руками (а не надиктовал). Только это показывает
 * «Отправить»: над надиктованным текстом стрелка не нужна — им распоряжается
 * грейс-окно Свободного диалога. Пока флаг стоит, в строке ввода одна стрелка:
 * тумблер записи и кнопка Диалога уходят, чтобы правку нечем было испортить.
 * Саму диктовку набор руками НЕ глушит — текст можно дополнить голосом.
 */
let typedByHand = false;
/**
 * Текст поля на момент начала распознаваемой фразы: interim-кадры рисуются как
 * `база + черновик`. Набор руками переписывает базу (см. beginTextEditing) —
 * иначе следующий кадр распознавания затёр бы набранное.
 */
let livePhraseBase = '';
/**
 * Голос звучит прямо сейчас — микрофон мигает. Сигналы речи разные у двух
 * движков (interim-кадр браузерного STT, RMS выше порога у серверной записи),
 * но «человек замолчал» ни один отдельным событием не сообщает: мигание живёт
 * SPEECH_BLINK_HOLD_MS от последнего сигнала и гаснет само.
 */
let speechActive = false;
let speechHoldTimer: ReturnType<typeof setTimeout> | null = null;
const SPEECH_BLINK_HOLD_MS = 600;
/** Простой Свободного диалога, после которого режим гаснет сам (см. startFreeIdleWatch). */
const FREE_IDLE_TIMEOUT_MS = 60_000;
const FREE_IDLE_POLL_MS = 5000;
let freeIdleSince = 0;
let freeIdleTimer: ReturnType<typeof setInterval> | null = null;
/**
 * Портретный мобильный — два экрана (видео / чат): уход на чат сам глушит
 * микрофон, возврат на видео возвращает его. Флаг помнит, что глушил именно
 * переход экрана: пауза, поставленная рукой пользователя, при возврате НЕ
 * снимается — его выбор старше нашей автоматики. См. setupPortraitScreens.
 */
let screenAutoMuted = false;
let portraitScreensObserver: IntersectionObserver | null = null;
const PORTRAIT_SCREENS_MQ = '(max-width: 900px) and (orientation: portrait)';
/** Следит за высотой сцены и ужимает колонку аватара до ширины кадра 9:16. */
let stageColumnObserver: ResizeObserver | null = null;
let micState: MicState = 'idle';
/** Не переподнимать распознавание чаще, чем раз в это время (защита от спина
 * на «no-speech»: браузер завершает сессию мгновенно, если тишина). */
let freeRearmAt = 0;
/**
 * «Не расслышал» — молчание в Свободном диалоге переподнимает запись само, и
 * каждая пустая расшифровка раньше грозила тостом (троттлинг был 60с). Человек,
 * который просто думает над ответом, получал упрёк раз в минуту.
 *
 * Теперь сообщение — признак того, что разговор реально заглох: показываем не
 * раньше, чем через пять минут НЕПРЕРЫВНОГО молчания, и повторяем не чаще, чем
 * раз в те же пять минут. Любая распознанная реплика обнуляет серию.
 */
const NO_SPEECH_QUIET_MS = 5 * 60 * 1000;
/** Начало текущей серии пустых распознаваний (0 — серии нет). */
let noSpeechSince = 0;
/** Когда сообщение показали в последний раз. */
let noSpeechToastAt = 0;

function notifyNoSpeech(message: string): void {
  const now = Date.now();
  if (!noSpeechSince) noSpeechSince = now;
  if (now - noSpeechSince < NO_SPEECH_QUIET_MS) return;
  if (now - noSpeechToastAt < NO_SPEECH_QUIET_MS) return;
  noSpeechToastAt = now;
  showToast(message, 'info');
}

/** Пользователь заговорил (или разговор перезапустили) — серия молчания прервана. */
function resetNoSpeechWatch(): void {
  noSpeechSince = 0;
  noSpeechToastAt = 0;
}

function updateMicToggle(): void {
  const btn = $('adlg-mic-toggle');
  if (!btn) return;
  // Тумблер записи — только для живого режима и только вне правки текста.
  btn.classList.toggle('hidden', !isLiveMode() || isTextEditing());
  btn.classList.toggle('adlg-mic-muted', micMuted);
  btn.classList.toggle('adlg-mic-active', !micMuted && micState === 'listening');
  // Мигает, пока звучит голос: видно, что запись действительно берёт речь, а
  // не просто «микрофон включён».
  btn.classList.toggle('adlg-mic-speaking', !micMuted && speechActive);
  btn.setAttribute('aria-pressed', micMuted ? 'true' : 'false');
  // В Свободном диалоге этот же тумблер — пауза диктовки: подписи говорят о
  // записи, а не о «выключить микрофон вообще».
  const title = freeDialogEnabled
    ? (micMuted ? 'Продолжить запись' : 'Пауза — остановить запись и поправить текст')
    : (micMuted ? 'Включить микрофон' : 'Выключить микрофон');
  btn.setAttribute('aria-label', title);
  btn.setAttribute('title', title);
  btn.querySelector('.adlg-mic-on')?.classList.toggle('hidden', micMuted);
  btn.querySelector('.adlg-mic-off')?.classList.toggle('hidden', !micMuted);
}

/**
 * Главное действие — одна кнопка на три состояния, как в Разговоре:
 * ход в работе → «Стоп»; в поле есть текст → «Отправить»; поле пустое →
 * «Свободный диалог».
 */
function updatePrimaryAction(): void {
  const stopBtn = $('adlg-stop-btn');
  const sendBtn = $('adlg-send-btn');
  const freeBtn = $('adlg-free-btn');
  if (!stopBtn || !sendBtn || !freeBtn) return;

  const live = isLiveMode();
  const hasText = !!($('adlg-input') as HTMLTextAreaElement | null)?.value.trim();
  const editing = isTextEditing();
  // «Стоп» — с момента отправки вопроса, а не с первого звука: между ними
  // проходят секунды рендера, и раньше в этом окне отменить ход было нечем.
  const busy = live && (isSending || isAvatarSpeaking());
  // Свободный диалог — режим живого разговора; в scripted-режиме его нет.
  // Во включённом режиме кнопка не прячется и при непустом поле: диктовка
  // копит текст в поле, а Стоп режима должен оставаться под рукой.
  // Пока идёт набор руками, режим не переключают: в строке ввода одна стрелка,
  // а кнопка Диалога возвращается по ней же — и снова пульсирует, слушая
  // продолжение (см. обработчик adlg-send-btn).
  const showFree = live && !busy && !editing && (freeDialogEnabled || !hasText);
  // «Отправить» — строго после набора руками: надиктованным текстом
  // распоряжается грейс-окно Свободного диалога, стрелка над ним лишняя.
  const showSend = !busy && hasText && typedByHand;

  stopBtn.classList.toggle('hidden', !busy);
  sendBtn.classList.toggle('hidden', !showSend);
  freeBtn.classList.toggle('hidden', !showFree);

  freeBtn.classList.toggle('adlg-free-active', freeDialogEnabled);
  freeBtn.classList.toggle(
    'adlg-free-listening',
    freeDialogEnabled && !micMuted && micState === 'listening'
  );
  freeBtn.setAttribute('aria-pressed', freeDialogEnabled ? 'true' : 'false');

  // Готовность голоса видна ДО того, как человек заговорил: пока идёт проба —
  // кнопка ВЫКЛЮЧЕНА и говорит об этом, без голоса вовсе — выключена насовсем.
  // Приглушённой, но нажимаемой, она обещала больше, чем могла: человек жал,
  // начинал говорить и получал «Не расслышал» — проба к тому моменту ещё не
  // решила, чем распознавать.
  const hint = voiceReadinessHint();
  freeBtn.classList.toggle('adlg-free-pending', voiceReadiness === 'probing');
  (freeBtn as HTMLButtonElement).disabled =
    voiceReadiness === 'unavailable' || voiceReadiness === 'probing';
  freeBtn.setAttribute('title', hint || 'Свободный диалог — говорите, аватар отвечает без кнопок');
  freeBtn.setAttribute('aria-label', hint || 'Свободный диалог');

  updateStageVoiceButton();
}

/**
 * Кнопка голоса на ПЕРВОМ экране портретного мобильного — там, где человек
 * смотрит на аватара и говорит. Раньше Свободный диалог жил только в строке
 * ввода на втором экране: чтобы заговорить, приходилось листать вниз, нажимать
 * и листать обратно — а каждое перелистывание ещё и ставило автопаузу.
 *
 * Она же — признак записи: бегущая волна отличает «микрофон слушает» от просто
 * включённого режима, а ускоренная волна — «ваш голос берётся прямо сейчас».
 */
function updateStageVoiceButton(): void {
  const btn = $('adlg-stage-free') as HTMLButtonElement | null;
  if (!btn) return;
  // В сценарном режиме Свободного диалога нет — кнопке на кадре делать нечего.
  btn.classList.toggle('hidden', !isLiveMode());

  const listening = freeDialogEnabled && !micMuted && micState === 'listening';
  btn.classList.toggle('adlg-stage-free-on', freeDialogEnabled);
  btn.classList.toggle('adlg-wave-live', listening);
  btn.classList.toggle('adlg-wave-speech', listening && speechActive);
  btn.classList.toggle('adlg-free-pending', voiceReadiness === 'probing');
  btn.disabled = voiceReadiness === 'unavailable' || voiceReadiness === 'probing';
  btn.setAttribute('aria-pressed', freeDialogEnabled ? 'true' : 'false');

  const label = $('adlg-stage-free-label');
  if (label) {
    // «Слушаю» — вход открыт и ждёт; «Записываю» — голос идёт прямо сейчас.
    // Разными их делает не только подпись, но и цвет волны (см. --adlg-rec):
    // на глаз одна скорость анимации эти состояния не разделяла.
    //
    // «Настройка» — проба голоса ещё идёт. Подпись важнее приглушённого вида:
    // человек видит, что система занята делом, а не сломана, и не начинает
    // говорить в микрофон, про который ещё ничего не известно.
    label.textContent = voiceReadiness === 'probing'
      ? 'Настройка'
      : listening
        ? (speechActive ? 'Записываю' : 'Слушаю')
        : (freeDialogEnabled && micMuted ? 'Пауза' : 'Голос');
  }
  const hint = voiceReadinessHint()
    || (freeDialogEnabled ? 'Остановить Свободный диалог' : 'Свободный диалог — говорите, аватар отвечает без кнопок');
  btn.setAttribute('title', hint);
  btn.setAttribute('aria-label', hint);
}

/**
 * Поле вопроса растёт под текст, как в Разговоре. Надиктованная реплика — это
 * несколько строк сразу, а зафиксированные две прятали её почти целиком:
 * пользователь не видел, что именно распознал микрофон, и не мог это править.
 */
function autoResizeDialogInput(): void {
  const input = $('adlg-input') as HTMLTextAreaElement | null;
  if (!input) return;
  const maxHeight = Math.min(200, Math.max(88, Math.floor(window.innerHeight * 0.28)));
  input.style.height = 'auto';
  input.style.height = Math.min(input.scrollHeight, maxHeight) + 'px';
}

function setMicState(next: MicState): void {
  micState = next;
  // Запись кончилась — мигать нечему, даже если последний сигнал речи был
  // только что.
  if (next !== 'listening') stopSpeechActivity();
  updateMicToggle();
  updatePrimaryAction();
}

/** Сигнал речи от любого из движков — микрофон мигает ещё SPEECH_BLINK_HOLD_MS. */
function markSpeechActivity(): void {
  noteFreeDialogActivity();
  if (speechHoldTimer) clearTimeout(speechHoldTimer);
  speechHoldTimer = setTimeout(() => {
    speechHoldTimer = null;
    setSpeechActive(false);
  }, SPEECH_BLINK_HOLD_MS);
  setSpeechActive(true);
}

/** Человек молчит/запись выключена — мигание гасим сразу. */
function stopSpeechActivity(): void {
  if (speechHoldTimer) {
    clearTimeout(speechHoldTimer);
    speechHoldTimer = null;
  }
  setSpeechActive(false);
}

function setSpeechActive(active: boolean): void {
  if (speechActive === active) return;
  speechActive = active;
  updateMicToggle();
  // Волна на кадре живёт этим же признаком — иначе «вас записывают» видно
  // только на втором экране, где кнопки.
  updateStageVoiceButton();
}

/** Гасит текущее распознавание (браузерное и серверное) без смены режима. */
function stopListening(): void {
  if (liveRecognition) {
    const rec = liveRecognition;
    liveRecognition = null;
    try { rec.stop(); } catch { /* уже остановлено */ }
  }
  stopServerSttRecording();
  setMicState('idle');
}

/**
 * Тумблер микрофона. В Свободном диалоге это пауза диктовки: клик глушит
 * запись (текст остаётся в поле на правку, начатая серверная запись
 * дописывается в поле асинхронно — распознанный кусок фразы не пропадает),
 * повторный клик продолжает диктовку с дописыванием. Сам режим при этом НЕ
 * выключается — его Старт/Стоп остаётся кнопкой Свободного диалога.
 */
function setMicMuted(muted: boolean, opts: { keepStream?: boolean } = {}): void {
  voiceLog('setMicMuted', { muted, freeDialogEnabled, micState, keepStream: !!opts.keepStream });
  micMuted = muted;
  if (muted) {
    // Пауза снимает отложенную авто-отправку: иначе через грейс-окно ход ушёл
    // бы недоправленным прямо из-под руки редактирующего.
    cancelFreeAutoSend();
    stopListening();
    // Отпускаем устройство, чтобы в браузере погас индикатор записи — но
    // ТОЛЬКО когда паузу поставил человек. Автоматическая пауза (перелистывание
    // экранов на телефоне) поток сохраняет: свежий getUserMedia на Android
    // отдаёт первые секунды почти в тишине, пока раскачивается АРУ, и реплика
    // сразу после возврата к видео распознавалась в ноль (прод 06.08, 15:21:29:
    // пик 0.0068 против 0.39 у следующей записи на том же потоке).
    if (!opts.keepStream) releaseMicStream();
  } else {
    resetNoSpeechWatch();
    freeRearmAt = 0;
    void warmUpDialogMic();
  }
  updateMicToggle();
  updatePrimaryAction();
}

function setFreeDialogEnabled(enabled: boolean): void {
  voiceLog('setFreeDialogEnabled', { enabled, micMuted, micState, browserSttBroken, micAccessBlocked });
  if (enabled) reportVoiceTrace('free-dialog-on');
  freeDialogEnabled = enabled;
  cancelFreeAutoSend();
  if (enabled) {
    // Включать режим при выключенном микрофоне бессмысленно — снимаем тумблер.
    if (micMuted) setMicMuted(false);
    freeRearmAt = 0;
    resetNoSpeechWatch();
    startFreeIdleWatch();
  } else {
    stopListening();
    stopFreeIdleWatch();
  }
  updateMicToggle();
  updatePrimaryAction();
}

/**
 * Простой Свободного диалога: минута без единого признака разговора — и режим
 * выключается сам. Забытая вкладка с включённым режимом держит микрофон
 * открытым, а на серверном STT ещё и шлёт в платное распознавание запись за
 * записью (та самая ровная «гребёнка» в логах прода).
 *
 * Простоем НЕ считаются ход в работе и говорящий аватар: человек в это время
 * слушает ответ, а не бездействует.
 */
function noteFreeDialogActivity(): void {
  freeIdleSince = Date.now();
}

function startFreeIdleWatch(): void {
  stopFreeIdleWatch();
  noteFreeDialogActivity();
  freeIdleTimer = setInterval(() => {
    if (!freeDialogEnabled) {
      stopFreeIdleWatch();
      return;
    }
    // Чтение чата на втором экране (screenAutoMuted) — не простой: человек
    // в разговоре, просто перелистнул на текст.
    if (isSending || isAvatarSpeaking() || speechActive || screenAutoMuted) {
      noteFreeDialogActivity();
      return;
    }
    if (Date.now() - freeIdleSince < FREE_IDLE_TIMEOUT_MS) return;
    setFreeDialogEnabled(false);
    showToast('Свободный диалог выключен — минута тишины', 'info');
  }, FREE_IDLE_POLL_MS);
}

function stopFreeIdleWatch(): void {
  if (freeIdleTimer) {
    clearInterval(freeIdleTimer);
    freeIdleTimer = null;
  }
}

/**
 * Уход на экран чата: живой микрофон глушится сам — в композере он виден
 * зачёркнутым, понятно, почему пауза. Флаг помнит, что глушила автоматика.
 */
function autoMuteForChatScreen(): void {
  if (!window.matchMedia(PORTRAIT_SCREENS_MQ).matches) return;
  if (isLiveMode() && !micMuted && (freeDialogEnabled || micState !== 'idle')) {
    screenAutoMuted = true;
    // Поток держим: человек ушёл читать ответ и вернётся говорить через
    // пару секунд — переоткрытый микрофон встретил бы его глухим.
    setMicMuted(true, { keepStream: true });
  }
}

/** Возврат к видео: снимаем ТОЛЬКО свою паузу — ручной тумблер старше. */
function restoreMicAfterChatScreen(): void {
  if (!screenAutoMuted) return;
  screenAutoMuted = false;
  if (micMuted) setMicMuted(false);
}

/**
 * Десктоп: колонка аватара обнимает кадр 9:16 без пустых полей. Ширина кадра
 * выводится из его высоты (aspect-ratio), а высота — из высоты окна, поэтому
 * ширину колонки CSS сам посчитать не может: замер идёт здесь и уходит в
 * --adlg-col-w (см. grid-template-columns у .adlg-layout). Обратной связи нет —
 * ширина колонки высоту сцены не меняет, наблюдатель не зацикливается.
 */
function syncStageColumnWidth(): void {
  const stage = document.querySelector<HTMLElement>('#view-avg-dialog .adlg-avatar-stage');
  const layout = document.querySelector<HTMLElement>('#view-avg-dialog .adlg-layout');
  if (!stage || !layout) return;
  const h = stage.getBoundingClientRect().height;
  if (h < 100) return; // свёрнутое вью / портретная раскладка — не мешаем
  // +2px на рамку сцены, иначе кадр каждый раз на волос уже колонки.
  const w = Math.round((h * 9) / 16) + 2;
  const prev = parseInt(layout.style.getPropertyValue('--adlg-col-w'), 10) || 0;
  if (Math.abs(prev - w) > 1) layout.style.setProperty('--adlg-col-w', `${w}px`);
}

function setupStageColumnSync(): void {
  syncStageColumnWidth();
  if (stageColumnObserver || typeof ResizeObserver === 'undefined') return;
  const stage = document.querySelector<HTMLElement>('#view-avg-dialog .adlg-avatar-stage');
  if (!stage) return;
  stageColumnObserver = new ResizeObserver(() => syncStageColumnWidth());
  stageColumnObserver.observe(stage);
}

/**
 * Портретный мобильный — два экрана со snap-скроллом (CSS-блок
 * orientation: portrait): экран 1 — видео, экран 2 — чат и кнопки. Наблюдатель
 * ловит СВАЙП между экранами (кнопки «Чат»/«Видео» дёргают те же хелперы
 * синхронно): чат занял больше половины вьюпорта — пауза, вернулись — микрофон
 * обратно, диалог продолжается.
 */
function setupPortraitScreens(): void {
  if (portraitScreensObserver || typeof IntersectionObserver === 'undefined') return;
  const root = $('adlg-root');
  const chatScreen = document.querySelector('#view-avg-dialog .adlg-conversation-panel');
  if (!root || !chatScreen) return;
  portraitScreensObserver = new IntersectionObserver((entries) => {
    // На широких экранах оба «экрана» видны одновременно — автоматика молчит.
    if (!window.matchMedia(PORTRAIT_SCREENS_MQ).matches) return;
    const entry = entries[entries.length - 1];
    if (!entry) return;
    if (entry.intersectionRatio >= 0.5) {
      autoMuteForChatScreen();
    } else {
      restoreMicAfterChatScreen();
    }
  }, { root, threshold: [0.5] });
  portraitScreensObserver.observe(chatScreen);
}

/** Человек набирает текст руками: в строке ввода одна стрелка «Отправить». */
function isTextEditing(): boolean {
  return typedByHand;
}

/**
 * Набор руками. Диктовку он НЕ выключает — надиктованное можно дополнить
 * клавиатурой и наоборот, — но делает две вещи, без которых текст терялся:
 *  1) снимает взведённое грейс-окно, иначе фраза ушла бы аватару прямо
 *     из-под руки набирающего;
 *  2) переносит базу interim-кадров на текущее содержимое поля, иначе
 *     следующий кадр распознавания затёр бы набранное.
 * Событие input поднимается только действиями пользователя, поэтому
 * программная запись поля (распознанный текст) сюда не попадает.
 */
function beginTextEditing(): void {
  const input = $('adlg-input') as HTMLTextAreaElement | null;
  // Поле опустело — набора нет: кнопки возвращаются сразу, не дожидаясь
  // отправки, иначе в строке ввода не осталось бы ни одной.
  if (!input?.value.trim()) {
    endTextEditing();
    return;
  }
  typedByHand = true;
  noteFreeDialogActivity();
  cancelFreeAutoSend();
  livePhraseBase = input.value.trim();
  updateMicToggle();
}

/**
 * Фокус поля поставлен КОДОМ, а не пальцем: распознанный на паузе текст сам
 * уводит курсор в поле (см. putTranscriptIntoInput). Считать это правкой
 * нельзя — правка прячет тумблер микрофона, и продолжить диктовку стало бы
 * нечем.
 */
let suppressFocusEditing = false;

/** Набор кончился (отправка, пустое поле, перезапуск) — кнопки возвращаются. */
function endTextEditing(): void {
  if (!typedByHand) return;
  typedByHand = false;
  updateMicToggle();
  updatePrimaryAction();
}

/** Последняя причина пропуска перевзвода — чтобы не писать её тиком (100 мс). */
let rearmSkipLogged = '';

function cancelFreeAutoSend(): void {
  if (freeAutoSendTimer) {
    clearTimeout(freeAutoSendTimer);
    freeAutoSendTimer = null;
  }
}

/**
 * Авто-отправка Свободного диалога: конец фразы взводит грейс-окно, а не шлёт
 * ход мгновенно. Продолжение речи (interim браузерного STT или RMS-детектор
 * серверной записи) и «Пауза» снимают таймер; дожившее до конца окно отправляет
 * всё поле целиком — вместе с тем, что пользователь успел набрать руками.
 */
function scheduleFreeAutoSend(): void {
  cancelFreeAutoSend();
  freeAutoSendTimer = setTimeout(() => {
    freeAutoSendTimer = null;
    if (!freeDialogEnabled || micMuted || isSending) return;
    const text = ($('adlg-input') as HTMLTextAreaElement | null)?.value.trim() || '';
    if (!text) return;
    // Перевзведённая к этому моменту пустая запись глушится, чтобы не поймать
    // хвост собственной отправки; после ответа режим перевзведётся сам.
    if (liveRecordingMeta) liveRecordingMeta.abandonedByTurn = true;
    stopListening();
    void sendQuestion(text, { bargeIn: bargeInAllowed() });
  }, FREE_AUTOSEND_GRACE_MS);
}

/**
 * Свободный диалог слушает только в паузе: пока аватар говорит, вход занят им
 * (перебивание ловит VAD по громкости), иначе собственный голос аватара из
 * колонок распознавался бы и уходил новым вопросом.
 */
function maybeRearmFreeDialog(): void {
  if (!freeDialogEnabled) return;
  if (micMuted || !isLiveMode()) {
    // Тик идёт каждые 100 мс — пишем только смену состояния, иначе пауза на
    // время ответа аватара забивает кольцо трейса целиком.
    const key = `${micMuted}|${isLiveMode()}`;
    if (rearmSkipLogged !== key) {
      rearmSkipLogged = key;
      voiceLog('перевзвод пропущен', { micMuted, live: isLiveMode() });
    }
    return;
  }
  rearmSkipLogged = '';
  // Доступ не выдан — молчим до жеста пользователя (см. micAccessBlocked).
  if (micAccessBlocked) return;
  if (isSending || isAvatarSpeaking()) return;
  if (micState !== 'idle' || liveRecognition || liveRecorder) return;
  if (Date.now() < freeRearmAt) return;
  freeRearmAt = Date.now() + 800;
  startLiveVoiceInput();
}

/**
 * Распознанный голос (браузерный и серверный STT) всегда падает в поле ввода —
 * пользователь видит и может править то, что распознал микрофон. В Свободном
 * диалоге конец фразы дополнительно взводит авто-отправку с грейс-окном
 * (scheduleFreeAutoSend): реплика уходит сама, но остаётся время нажать
 * «Паузу» и договорить или поправить текст.
 *
 * `keepInInput` глушит авто-отправку — им помечается заведомо НЕДОГОВОРЁННАЯ
 * фраза (запись оборвана капом на полуслове). Отправить такой огрызок ходом
 * значит получить ответ аватара на половину вопроса, а вторую половину —
 * следующим ходом поверх него.
 */
function putTranscriptIntoInput(transcript: string, opts: { keepInInput?: boolean } = {}): void {
  const text = transcript.trim();
  if (!text) return;
  resetNoSpeechWatch();
  noteFreeDialogActivity();
  const input = $('adlg-input') as HTMLTextAreaElement | null;
  if (!input) return;
  const existing = input.value.trim();
  input.value = existing ? `${existing} ${text}` : text;
  // В активной диктовке поле не фокусируем: на мобильном фокус открывает
  // клавиатуру поверх разговора без рук. На паузе фокус уместен — правка.
  if (!freeDialogEnabled || micMuted) {
    suppressFocusEditing = true;
    input.focus();
    // Снимаем в следующем такте: обработчик focus отработает синхронно внутри
    // focus(), но браузеры отправляют его и отложенно — держим до конца тика.
    setTimeout(() => { suppressFocusEditing = false; }, 0);
  }
  autoResizeDialogInput();
  // Длинную диктовку показываем с конца — там продолжение фразы.
  input.scrollTop = input.scrollHeight;
  updatePrimaryAction();
  if (freeDialogEnabled && !micMuted && !opts.keepInInput) scheduleFreeAutoSend();
}

/**
 * Движок Web Speech в браузере есть, но сервис за ним недоступен: 'network' —
 * Chrome без доступа к сервису Google (типичный случай в РФ), 'service-not-allowed' /
 * 'language-not-supported' — сервис запрещён или не знает русского. До конца
 * жизни страницы ходим сразу серверным распознаванием — не платим таймаутом
 * движка за каждую фразу.
 */
let browserSttBroken = false;
/** Профиль без серверного провайдера и браузер без Web Speech: голосу не на
 * чем работать. Сообщаем один раз — Свободный диалог перевзводит распознавание
 * тиком, и тост на каждый перевзвод превратился бы в спам. */
let voiceUnavailableNotified = false;

function speechRecognitionCtor(): (new () => LiveSpeechRecognition) | undefined {
  const w = window as unknown as {
    SpeechRecognition?: new () => LiveSpeechRecognition;
    webkitSpeechRecognition?: new () => LiveSpeechRecognition;
  };
  return w.SpeechRecognition || w.webkitSpeechRecognition;
}

/**
 * Приговор движку распознавания ЗАПОМИНАЕТСЯ на устройстве.
 *
 * На Huawei и прочих Chromium без сервисов Google webkitSpeechRecognition
 * существует, но за ним нет сервиса: он молча завершает сеанс без единого
 * кадра. Выясняется это только опытом — двумя пустыми сеансами по 5 секунд.
 * Приговор жил до перезагрузки страницы, поэтому КАЖДЫЙ заход в Диалог
 * начинался с 11 секунд глухого микрофона (прод 06.08, сессия 13:36:32 →
 * серверный STT только в 13:36:43). Теперь опыт переживает перезагрузку.
 *
 * Привязан к строке браузера и живёт 30 суток: обновился браузер или прошёл
 * месяц — пробуем движок заново, вдруг сервис появился.
 */
const STT_ENGINE_VERDICT_KEY = 'avg_stt_engine_verdict';
const STT_ENGINE_VERDICT_TTL_MS = 30 * 24 * 60 * 60 * 1000;

function rememberBrowserSttBroken(): void {
  try {
    localStorage.setItem(
      STT_ENGINE_VERDICT_KEY,
      JSON.stringify({ broken: true, ua: navigator.userAgent, at: Date.now() })
    );
  } catch {
    /* приватный режим — переживём, просто проверим движок в следующий заход */
  }
}

function browserSttBrokenRemembered(): boolean {
  try {
    const raw = localStorage.getItem(STT_ENGINE_VERDICT_KEY);
    if (!raw) return false;
    const v = JSON.parse(raw) as { broken?: boolean; ua?: string; at?: number };
    if (!v.broken || v.ua !== navigator.userAgent) return false;
    return Date.now() - (v.at || 0) < STT_ENGINE_VERDICT_TTL_MS;
  } catch {
    return false;
  }
}

/** Движок признан неработающим — здесь и сейчас, и на будущее. */
function markBrowserSttBroken(): void {
  browserSttBroken = true;
  rememberBrowserSttBroken();
  // Приговор меняет способ распознавания — подпись кнопки об этом знает.
  updatePrimaryAction();
}

/**
 * Готовность голосового ввода — ЭТАП ИНИЦИАЛИЗАЦИИ сессии, а не побочный
 * результат первой попытки заговорить.
 *
 * До этой пробы Свободный диалог выяснял всё в бою: включён режим — а чем
 * распознавать, выдан ли доступ и есть ли вообще на чём работать, становилось
 * известно секунд через десять, и всё это время человек говорил в глухой
 * микрофон, не зная об этом. Теперь состояние известно до первого слова и
 * видно на кнопке.
 *
 *  probing     — проба идёт (доступ к микрофону, вердикт движка);
 *  ready       — распознавать есть чем, доступ выдан;
 *  blocked     — доступа к микрофону нет (нужен жест человека);
 *  unavailable — ни движка в браузере, ни серверного STT в профиле.
 */
type VoiceReadiness = 'probing' | 'ready' | 'blocked' | 'unavailable';
let voiceReadiness: VoiceReadiness = 'probing';
/** Человек нажал «Свободный диалог», пока шла проба: включим, как будем готовы. */
let freeDialogWantedWhenReady = false;

function setVoiceReadiness(next: VoiceReadiness): void {
  if (voiceReadiness === next) return;
  voiceReadiness = next;
  voiceLog('готовность голоса', { state: next, browserSttBroken });
  if (next === 'unavailable' && !voiceUnavailableNotified) {
    voiceUnavailableNotified = true;
    showToast('Голосовой ввод в этом браузере недоступен — напишите вопрос текстом', 'error');
  }
  if (next === 'ready' && freeDialogWantedWhenReady) {
    freeDialogWantedWhenReady = false;
    if (!freeDialogEnabled) setFreeDialogEnabled(true);
  }
  updatePrimaryAction();
  updateMicToggle();
}

/** Чем именно проба закончилась плохо — иначе подсказка врёт про доступ. */
let voiceBlockedReason: 'no_access' | 'silent_mic' = 'no_access';

/** Подпись состояния голоса для кнопки; null — всё готово, подпись обычная. */
function voiceReadinessHint(): string | null {
  if (voiceReadiness === 'probing') return 'Настройка голоса — секунду';
  if (voiceReadiness === 'blocked') {
    return voiceBlockedReason === 'silent_mic'
      ? 'Микрофон не даёт звука — проверьте, не занят ли он другим приложением, и нажмите ещё раз'
      : 'Нужен доступ к микрофону — нажмите и разрешите';
  }
  if (voiceReadiness === 'unavailable') return 'Голос недоступен — напишите вопрос текстом';
  return null;
}

/**
 * Хост страницы. Его ставит обёртка, если Диалог открыт не сам по себе:
 * Mini App Telegram — это `telegram-miniapp` (см. `telegram-app.js` в
 * messenger-gateway).
 */
function dialogHost(): string {
  return String((window as unknown as { AVG_DIALOG_HOST?: string }).AVG_DIALOG_HOST || '');
}

/**
 * Обещание со сроком: то, что не ответило вовремя, перестаёт нас держать.
 *
 * Нужен потому, что часть браузерных обещаний не отклоняется НИКОГДА — они
 * просто ждут условия (жеста человека, например). Внутри пробы готовности это
 * означает вечную «Настройку» на кнопке голоса.
 */
async function withDeadline<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise.catch(() => undefined),
    new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), ms)),
  ]);
}

/**
 * Микрофон отдаёт звук, а не цифровую тишину.
 *
 * Проверка не про громкость: тихая комната даёт маленький, но НЕнулевой
 * уровень. Ровно нули за всё окно — это занятый другим приложением вход,
 * выключенный системой микрофон или мёртвая дорожка; в таком состоянии человек
 * говорит впустую и слышит от нас «Не расслышал».
 *
 * Заодно это окно прогревает АРУ: мобильный вход отдаёт первые доли секунды
 * почти в тишине, и раньше в них попадало начало первой фразы.
 *
 * `null` — судить нечем (нет WebAudio или контекст не пустили без жеста):
 * в этом случае голос не блокируем, недоказанное не считаем сломанным.
 */
async function micDeliversSound(stream: MediaStream, windowMs = 700): Promise<boolean | null> {
  const Ctor = (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
    .AudioContext
    || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;

  let ctx: AudioContext | null = null;
  try {
    ctx = new Ctor();
    if (ctx.state === 'suspended') {
      // ВАЖНО: `resume()` до касания человека НЕ отклоняется — он остаётся
      // висеть, пока касания не будет. Без срока это вешает всю пробу, а с ней
      // и кнопку голоса: «Настройка» навсегда. Ждём коротко и идём дальше —
      // недоказанное не считаем сломанным.
      await withDeadline(ctx.resume(), 400);
    }
    if (ctx.state !== 'running') return null;

    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const frame = new Float32Array(analyser.fftSize);

    // Окно проходим целиком, даже увидев звук в первом кадре: эти же
    // миллисекунды и есть прогрев входа перед первой фразой.
    let peak = 0;
    const startedAt = Date.now();
    while (Date.now() - startedAt < windowMs) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      analyser.getFloatTimeDomainData(frame);
      for (const sample of frame) peak = Math.max(peak, Math.abs(sample));
    }
    return peak > 0;
  } catch {
    return null;
  } finally {
    if (ctx) void ctx.close().catch(() => undefined);
  }
}

/**
 * Проба голоса на старте live-сессии. Микрофон здесь же и прогревается —
 * getUserMedia платится один раз, до первого клика, а не в момент реплики.
 */
async function initVoiceReadiness(): Promise<void> {
  voiceUnavailableNotified = false;
  freeDialogWantedWhenReady = false;
  voiceReadiness = 'probing';
  updatePrimaryAction();

  // Опыт прошлых заходов: движок на этом устройстве уже признан глухим.
  if (browserSttBrokenRemembered()) {
    browserSttBroken = true;
    voiceLog('движок признан глухим ранее — сразу серверный STT');
  }

  // Встроенный вебвью Telegram — не браузер: `webkitSpeechRecognition` в нём
  // объявлен, а сервиса распознавания за ним нет. Раньше это выяснялось
  // ОПЫТОМ — двумя пустыми сеансами, и человек слышал «Не расслышал» ровно
  // столько раз, сколько нужно было коду, чтобы поумнеть; хранилище вебвью
  // между запусками не переживает, поэтому платил он этим на каждом заходе
  // (прод 2026-08-10, Android 12). Здесь знание про хост есть заранее.
  //
  // Приговор НЕ запоминаем: он про хост, а не про устройство — в обычном
  // Chrome того же телефона движок работает.
  if (!browserSttBroken && dialogHost() === 'telegram-miniapp') {
    browserSttBroken = true;
    voiceLog('хост без движка распознавания — сразу серверный STT', { host: dialogHost() });
  }

  const hasEngine = !!speechRecognitionCtor() && !browserSttBroken;
  const serverProvider = (config?.stt_provider || 'browser').toLowerCase();
  const hasServerStt = serverProvider !== 'browser'
    && typeof MediaRecorder !== 'undefined'
    && !!navigator.mediaDevices?.getUserMedia;
  if (!hasEngine && !hasServerStt) {
    setVoiceReadiness('unavailable');
    return;
  }

  // Доступ к микрофону нужен обоим путям. Пока человек читает системный
  // запрос, состояние честно остаётся 'probing'.
  const stream = await acquireMicStream();
  if (!stream) {
    voiceBlockedReason = 'no_access';
    setVoiceReadiness('blocked');
    return;
  }

  // Доступ выдан — но выдан не значит слышит. Последняя проверка перед тем,
  // как отдать голос человеку: вход отдаёт звук, а не цифровую тишину.
  // Срок и на всю пробу целиком: она — удобство, а не условие разговора.
  // Зависшая проба означала бы кнопку «Настройка», из которой нет выхода.
  const sound = await withDeadline(micDeliversSound(stream), 2000);
  voiceLog('проба входа', { sound });
  if (sound === false) {
    voiceBlockedReason = 'silent_mic';
    setVoiceReadiness('blocked');
    return;
  }
  setVoiceReadiness('ready');
}

function startLiveVoiceInput(): void {
  const SpeechRec = speechRecognitionCtor();

  // Браузерное распознавание первично всегда, когда браузер его умеет: паузу
  // речи движок ловит сам, и ограничения серверной записи (кап 25 с, лимиты
  // SpeechKit) его не касаются. Серверный провайдер профиля (whisper/yandex/
  // silero_api) — фоллбек: браузеры без Web Speech (Firefox, часть мобильных
  // WebView) и браузеры со сломанным сервисом распознавания (browserSttBroken).
  // До этого профиль с облачным STT гнал ВСЕХ пользователей серверным путём —
  // на нём и висел Тренер (прод 03–04.08: ~90% пустых распознаваний).
  const serverProvider = (config?.stt_provider || 'browser').toLowerCase();
  voiceLog('startLiveVoiceInput', { hasEngine: !!SpeechRec, browserSttBroken, serverProvider, micState, emptyRecognitionRuns });
  if (!SpeechRec || browserSttBroken) {
    if (serverProvider !== 'browser') {
      void toggleServerSttRecording();
    } else if (!voiceUnavailableNotified) {
      voiceUnavailableNotified = true;
      showToast('Голосовой ввод недоступен в этом браузере', 'error');
    }
    return;
  }

  if (liveRecognition) {
    liveRecognition.stop();
    return;
  }

  const rec = new SpeechRec() as LiveSpeechRecognition;
  rec.lang = 'ru-RU';
  // Промежуточные результаты — это «запрос пишется прямо во время речи»: без
  // них пользователь до конца фразы не видит, что распознаёт микрофон.
  rec.interimResults = true;
  rec.continuous = false;
  liveRecognition = rec;
  // Захват звука реально начинается только с onstart (инициализация сервиса,
  // а при первом использовании — ещё и запрос разрешения на микрофон). Если
  // показать «Слушаю…» сразу, пользователь начинает говорить раньше времени и
  // первый вопрос теряется — до onstart держим «Подготовка…».
  setMicState('preparing');

  // База interim-кадров (`база + черновик`) живёт на модуле: набор руками
  // посреди фразы переносит её на набранное (см. beginTextEditing), иначе
  // следующий кадр распознавания затирал бы то, что человек только что напечатал.
  /** Этот сеанс распознавания дал хоть один кадр — движок реально работает. */
  let sawResult = false;
  let silentEngineTimer: ReturnType<typeof setTimeout> | null = null;

  rec.onstart = () => {
    voiceLog('rec.onstart');
    // Сторож «слушает, но глух»: движок стартовал и держит сессию открытой, а
    // результатов не отдаёт вовсе. Яндекс.Браузер на планшете Huawei (трейс
    // 2026-08-06): onstart есть, onresult и onend не приходят НИКОГДА — микрофон
    // молча «слушает» в пустоту, и ни счётчик пустых сеансов (он считает
    // onend), ни сторож старта (он ждёт onstart) не срабатывают. Без этого
    // таймера ввод висел вечно.
    if (silentEngineTimer) clearTimeout(silentEngineTimer);
    silentEngineTimer = setTimeout(() => {
      if (liveRecognition !== rec || sawResult) return;
      voiceLog('движок слушает, но не отдаёт результатов — уходим на сервер');
      reportVoiceTrace('browser-stt-silent-engine');
      fallbackToServerStt();
    }, BROWSER_STT_SILENT_TIMEOUT_MS);
    livePhraseBase = ($('adlg-input') as HTMLTextAreaElement | null)?.value.trim() || '';
    setMicState('listening');
  };

  rec.onresult = (event) => {
    const result = event.results[0];
    const transcript = result?.[0]?.transcript?.trim() || '';
    if (!transcript) return;
    sawResult = true;
    emptyRecognitionRuns = 0;
    if (silentEngineTimer) {
      clearTimeout(silentEngineTimer);
      silentEngineTimer = null;
    }
    voiceLog('rec.onresult', { isFinal: !!result?.isFinal, chars: transcript.length });
    // Речь продолжается — недосказанное не отправлять, микрофон мигает.
    cancelFreeAutoSend();
    markSpeechActivity();
    const input = $('adlg-input') as HTMLTextAreaElement | null;
    if (!result?.isFinal) {
      if (!input) return;
      input.value = livePhraseBase ? `${livePhraseBase} ${transcript}` : transcript;
      autoResizeDialogInput();
      input.scrollTop = input.scrollHeight;
      updatePrimaryAction();
      return;
    }
    // Финал фразы: убрать interim-черновик и дописать распознанное начисто.
    if (input) input.value = livePhraseBase;
    putTranscriptIntoInput(transcript);
  };

  rec.onerror = (event) => {
    setMicState('idle');
    liveRecognition = null;
    const code = event?.error || '';
    voiceLog('rec.onerror', { code });
    if (code === 'not-allowed') {
      // Не просто тост: без остановки перевзвода мобильный Chrome показывал бы
      // системный запрос заново каждые 800 мс.
      blockMicUntilUserGesture('Нет доступа к микрофону. Разрешите доступ и включите Диалог заново.');
      return;
    }
    // Сервис за движком недоступен — переключаемся на серверный фоллбек и
    // тут же перевзводим ввод им: текущая фраза пользователя ещё не сказана
    // до конца, терять её молча нельзя.
    if (code === 'network' || code === 'service-not-allowed' || code === 'language-not-supported') {
      markBrowserSttBroken();
      startLiveVoiceInput();
      return;
    }
    // 'no-speech' молча: браузер завершает сессию от любой паузы, а Свободный
    // диалог тут же переподнимает распознавание — сообщать не о чем.
  };

  rec.onend = () => {
    // После onerror с фоллбеком уже может идти серверная запись — не сбивать
    // её состояние микрофона: onend этого rec больше ничем не владеет.
    if (liveRecognition !== rec) return;
    liveRecognition = null;
    setMicState('idle');
    // Сеанс закончился, не дав НИ ОДНОГО кадра. Один такой — это обычное
    // молчание; несколько подряд означают, что движок в этом браузере не
    // распознаёт вовсе. Huawei и прочие устройства без сервисов Google: сам
    // webkitSpeechRecognition есть (Chromium), а сервиса за ним нет, и он
    // молча завершается вместо ошибки 'network'. Уходим на серверный путь —
    // там распознаёт наш SpeechKit.
    voiceLog('rec.onend', { sawResult, emptyRecognitionRuns });
    if (sawResult) return;
    emptyRecognitionRuns += 1;
    if (emptyRecognitionRuns < EMPTY_RECOGNITION_LIMIT) {
      reportVoiceTrace('browser-stt-empty-run');
      return;
    }
    markBrowserSttBroken();
    voiceLog('browserSttBroken: пустые сеансы подряд', { runs: emptyRecognitionRuns });
    reportVoiceTrace('browser-stt-empty-runs');
    if (freeDialogEnabled && !micMuted && !micAccessBlocked) startLiveVoiceInput();
  };

  // Движок может не стартовать вовсе и молча: на iPhone (WebKit) распознавание
  // требует пользовательского жеста, а Свободный диалог перевзводит микрофон
  // таймером — start() либо бросает, либо не доводит до onstart, и микрофон
  // навсегда застревал в 'preparing' (перевзвод пускает только из 'idle').
  // Оба случая ведут в серверный фоллбек — он жеста не требует.
  const fallbackToServerStt = (): void => {
    if (liveRecognition !== rec) return;
    if (silentEngineTimer) {
      clearTimeout(silentEngineTimer);
      silentEngineTimer = null;
    }
    voiceLog('fallbackToServerStt (движок не стартовал)');
    reportVoiceTrace('browser-stt-no-start');
    liveRecognition = null;
    try { rec.stop(); } catch { /* не стартовал */ }
    markBrowserSttBroken();
    setMicState('idle');
    startLiveVoiceInput();
  };

  try {
    rec.start();
  } catch {
    fallbackToServerStt();
    return;
  }
  // Сторож старта: onstart не пришёл — движка фактически нет. Срок зависит от
  // того, выдан ли уже доступ: живой микрофонный поток означает, что системный
  // запрос давно позади и ждать нечего; иначе даём заведомо больше времени —
  // человек читает запрос доступа, обрывать его нельзя. Проверять доступ через
  // Permissions API нельзя: Chromium-форки (Яндекс.Браузер, оболочки Huawei)
  // отвечают 'prompt' даже после выдачи, и сторож не взводился вовсе — на них
  // микрофон оставался в «Подготовке…» навсегда.
  const startDeadline = micStreamIsLive(liveMicStream)
    ? BROWSER_STT_START_TIMEOUT_MS
    : BROWSER_STT_PERMISSION_TIMEOUT_MS;
  setTimeout(() => {
    if (liveRecognition === rec && micState === 'preparing') fallbackToServerStt();
  }, startDeadline);
}

/** Сколько ждём onstart браузерного распознавания, прежде чем счесть его мёртвым. */
const BROWSER_STT_START_TIMEOUT_MS = 2500;
/** То же, но пока доступ к микрофону ещё не выдан: человек читает системный запрос. */
const BROWSER_STT_PERMISSION_TIMEOUT_MS = 12000;
/**
 * Сколько движок вправе «слушать» без единого результата, прежде чем счесть его
 * глухим. Живой движок отдаёт interim-кадры с первых слогов, а сессию без речи
 * закрывает сам (no-speech) за считаные секунды — молчание дольше означает, что
 * сервиса за движком нет.
 */
const BROWSER_STT_SILENT_TIMEOUT_MS = 8000;
/**
 * Сколько подряд пустых сеансов распознавания (ни одного кадра, ни одной
 * ошибки) считать приговором браузерному движку. Один пустой — обычное
 * молчание; несколько подряд бывают там, где `webkitSpeechRecognition`
 * существует, а сервиса за ним нет: устройства Huawei без сервисов Google,
 * Яндекс.Браузер и прочие Chromium-форки. Такой движок не шлёт 'network' —
 * он просто молча заканчивается, и без счётчика фоллбек не включался никогда.
 */
const EMPTY_RECOGNITION_LIMIT = 2;
let emptyRecognitionRuns = 0;

// ---- ВРЕМЕННОЕ: подробный трейс голосового ввода (этап отладки) ------------
//
// Снимается одним движением: VOICE_DEBUG = false (или localStorage
// avg_voice_debug = '0' у конкретного пользователя) — тогда voiceLog становится
// no-op, трейс на сервер не уходит. Существует ради устройств, где консоли под
// рукой нет (телефоны и планшеты Huawei без сервисов Google): трейс уезжает
// вместе с записью на /api/avg-dialog/stt и виден в журнале гейтвея.
//
// ВЫКЛЮЧЕН 06.08.2026: отладка мобильного голоса закончена, причины найдены
// (см. контракт распознавания). Пока false, речь пользователя и трейс никуда
// не уезжают — сервер пишет их только по присланному client_log, а его больше
// не присылают. Включать обратно только на время разбора конкретной жалобы.
//
// Включался 10.08.2026 на время разбора жалобы на голос в Mini App Telegram
// («не расслышал», накопление вопросов, ответ одним текстом). Разбор закончен,
// причины найдены и починены — ВЫКЛЮЧЕН обратно 11.08.2026 по слову владельца:
// речь человека и трейс его устройства больше в журнал не уезжают.
const VOICE_DEBUG = false;
// Кольцо на 40 строк выедалось тиком перевзвода: пока аватар говорит, в него
// падало по строке каждые 100 мс, и к моменту отправки трейса от разговора не
// оставалось ничего, кроме «перевзвод пропущен». Тик теперь пишется один раз на
// период (см. maybeRearmFreeDialog), а кольца хватает на несколько ходов.
const VOICE_LOG_LIMIT = 120;
const voiceLogRing: string[] = [];

function voiceDebugEnabled(): boolean {
  if (!VOICE_DEBUG) return false;
  try {
    return localStorage.getItem('avg_voice_debug') !== '0';
  } catch {
    return true;
  }
}

function voiceLog(event: string, data?: Record<string, unknown>): void {
  if (!voiceDebugEnabled()) return;
  const line = `${new Date().toISOString().slice(11, 23)} ${event}${data ? ' ' + JSON.stringify(data) : ''}`;
  voiceLogRing.push(line);
  if (voiceLogRing.length > VOICE_LOG_LIMIT) voiceLogRing.shift();
  // eslint-disable-next-line no-console
  console.log('[voice]', line);
  (window as unknown as { __voiceLog?: string[] }).__voiceLog = voiceLogRing;
}

function voiceLogTail(): string {
  return voiceLogRing.join('\n');
}

/** Когда какой повод отправляли — чтобы один и тот же не молотил в журнал. */
const voiceTraceSentAt = new Map<string, number>();
const VOICE_TRACE_THROTTLE_MS = 15000;

/** Отправляет накопленный трейс в журнал гейтвея (без аудио). */
function reportVoiceTrace(reason: string): void {
  if (!voiceDebugEnabled()) return;
  const prev = voiceTraceSentAt.get(reason) || 0;
  if (Date.now() - prev < VOICE_TRACE_THROTTLE_MS) return;
  voiceTraceSentAt.set(reason, Date.now());
  void fetch('/api/avg-dialog/stt', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + state.authToken },
    body: JSON.stringify({
      session_id: getSessionId(),
      client_log: `ua=${navigator.userAgent}\n${voiceLogTail()}`,
      reason,
    }),
  }).catch(() => undefined);
}

/**
 * Доступ к микрофону не выдан — перевзвод ОСТАНОВЛЕН до действия пользователя.
 *
 * Мобильный Chrome (2026-08-06): закрытый свайпом запрос разрешения — это не
 * «запретить», а «спросить снова». Свободный диалог перевзводит вход каждые
 * 800 мс, и каждый перевзвод показывал запрос заново — бесконечная карусель
 * системного диалога. Ставится при отказе/закрытии, снимается только явным
 * жестом (клик по микрофону или по кнопке Диалога), потому что жест и есть
 * законный повод спросить снова.
 */
let micAccessBlocked = false;

function blockMicUntilUserGesture(message: string): void {
  voiceLog('доступ к микрофону не выдан — перевзвод остановлен');
  reportVoiceTrace('mic-access-blocked');
  micAccessBlocked = true;
  setVoiceReadiness('blocked');
  if (freeDialogEnabled) setFreeDialogEnabled(false);
  stopListening();
  showToast(message, 'error');
}

/** Явный жест пользователя — снова можно просить доступ. */
function allowMicRequestsAgain(): void {
  micAccessBlocked = false;
}

/**
 * Разрешение уже выдано — значит, задержка старта не объясняется открытым
 * системным запросом, и сторож мёртвого движка вправе сработать. Где Permissions
 * API нет (WebKit/iPhone) — сторож нужен как раз там, поэтому true.
 */
async function micPermissionGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
    return !status || status.state === 'granted';
  } catch {
    return true;
  }
}

// ---- Серверный STT: запись MediaRecorder -> POST /avg-dialog/stt ----

/**
 * Жёсткий потолок серверной записи. SpeechKit v1 принимает СТРОГО меньше 30 с
 * аудио, а прежний кап был ровно 30 000 мс — каждая запись, дожившая до него,
 * отвергалась целиком («audio duration should be less than 30s»), и реплика
 * пропадала молча. 25 с оставляют запас на длительность контейнера и опоздание
 * таймера.
 */
const SERVER_STT_MAX_MS = 25000;
/** Шумовой пол калибровки на уровне прежнего фиксированного порога речи —
 * значит, запись началась ПОВЕРХ уже идущей речи (барж-ин): считать её порогом
 * нельзя, речь никогда бы его не пробила. */
const STT_SPEECH_ABS_RMS = 0.02;
/**
 * Порог речи в ЗАПИСИ — свой, отдельно от порога перебивания (VAD_*).
 * Мобильные (2026-08-06): Android отдаёт поток после подавления шума и АРУ —
 * речь в нём идёт заметно ниже 0.015, прежнего абсолютного минимума. Порог не
 * пробивался ни разу: кнопка микрофона не мигала (сигнала «говорит» не было), а
 * дорожка на onstop отбрасывалась как «тишина» — телефон «не слышал» голос.
 * Теперь минимум низкий, а от ложных срабатываний защищает относительное
 * правило (множитель и запас над измеренным шумовым полом). Порог перебивания
 * остался прежним: там ложный триггер обрывает аватара на полуслове.
 */
const STT_VAD_MULTIPLIER = 1.6;
const STT_VAD_MARGIN = 0.006;
const STT_VAD_MIN_RMS = 0.004;

/**
 * ВЫУЧЕННЫЙ ПОЛ ШУМА — подстройка по итогам распознавания, а не по громкости.
 *
 * Калибровка первых полсекунды знает только то, что было ДО фразы. Она не
 * отличает шум от речи: кондиционер, улица, чужой разговор пробивают порог,
 * запись уходит в распознавание и возвращается пустой — человек получает «Не
 * расслышал» на звук, которого не издавал (прод 2026-08-10: из десяти дорожек
 * семь пустых, пики 0.019–0.031).
 *
 * Отличить их можно ровно одним способом — спросить распознавание. Пустой
 * ответ на дорожку, где «речь была», означает: этот уровень на этом устройстве
 * речью НЕ является, порог надо поднять. Распознанный текст означает
 * обратное — на этом уровне речь есть, и порог обязан пропускать его впредь.
 *
 * Инварианты, без которых подстройка опасна:
 *  • успех распознавания ВСЕГДА сильнее шумного опыта (иначе тихий голос
 *    окажется отрезан навсегда);
 *  • выученный пол не поднимается выше абсолютного потолка речи, то есть
 *    оглохнуть до полной немоты система не может;
 *  • живёт в памяти вкладки: смена обстановки начинается с чистого листа.
 */
let learnedNoiseFloor = 0;
/** Насколько поднимать пол над уровнем шума, принятого за речь. */
const LEARN_NOISE_RAISE = 1.15;
/** Запас ПОД распознанной речью: порог обязан её пропускать с полем. */
const LEARN_SPEECH_DROP = 0.6;
/** Выше этого выученный пол не поднимается — иначе можно оглохнуть. */
const LEARN_FLOOR_CEILING = STT_SPEECH_ABS_RMS;

/**
 * Дорожку признали шумом: распознавание не нашло в ней ни слова, хотя
 * детектор считал это речью. Поднимаем пол — следующий такой звук записи не
 * начнёт.
 */
function learnNoiseFrom(meta: SttRecordingMeta): void {
  if (!meta.watching || !meta.hadSpeech) return;
  const peak = meta.peakRms || 0;
  if (peak <= 0) return;
  const next = Math.min(peak * LEARN_NOISE_RAISE, LEARN_FLOOR_CEILING);
  if (next <= learnedNoiseFloor) return;
  learnedNoiseFloor = next;
  voiceLog('шум: пол поднят', { peak: Number(peak.toFixed(4)), floor: Number(learnedNoiseFloor.toFixed(4)) });
}

/**
 * Дорожку распознали: на этом уровне человек РЕАЛЬНО говорит. Опускаем пол под
 * него — иначе выученный шум съел бы тихую речь того же человека.
 */
function learnSpeechFrom(meta: SttRecordingMeta): void {
  const peak = meta.peakRms || 0;
  if (peak <= 0 || !learnedNoiseFloor) return;
  const allowed = peak * LEARN_SPEECH_DROP;
  if (allowed >= learnedNoiseFloor) return;
  learnedNoiseFloor = Math.max(allowed, 0);
  voiceLog('речь: пол опущен', { peak: Number(peak.toFixed(4)), floor: Number(learnedNoiseFloor.toFixed(4)) });
}

/** Что видел детектор тишины за время записи — решает судьбу дорожки в onstop. */
type SttRecordingMeta = {
  /** Детектор реально работал (WebAudio доступен). false — судить не о чем. */
  watching: boolean;
  /** Хоть один момент речи был. */
  hadSpeech: boolean;
  /** Запись оборвана капом, пока пользователь ГОВОРИЛ: фраза не дописана. */
  cappedMidSpeech?: boolean;
  /** Самый громкий замер за запись и калиброванный шумовой пол — по ним
   * решается судьба дорожки, когда порог речи не пробит (см. onstop). */
  peakRms?: number;
  noiseFloor?: number;
  /** Запись оборвана не человеком и не тишиной, а уходом хода: это «окно
   * грейса» авто-отправки, а не реплика (см. scheduleFreeAutoSend). */
  abandonedByTurn?: boolean;
};

/** Метаданные ЗАПИСИ, которая идёт прямо сейчас (null — записи нет). */
let liveRecordingMeta: SttRecordingMeta | null = null;

let liveRecorder: MediaRecorder | null = null;
let silenceWatchCleanup: (() => void) | null = null;
/**
 * Микрофонный поток живёт МЕЖДУ фразами, а не выдаётся заново на каждый клик.
 *
 * getUserMedia — это открытие устройства: разрешение, выбор входа, запуск
 * цепочки шумоподавления. На Windows/Chrome оно занимает от сотен миллисекунд
 * до пары секунд, а прежний код звал его внутри обработчика клика и стартовал
 * MediaRecorder только после резолва. Пользователь начинает говорить сразу по
 * клику — и всё, что произнесено за это время, в запись не попадало. Отсюда
 * «микрофон обычно глотает первое слово».
 *
 * Поток закрывается при выходе из Диалога (releaseMicStream в teardown), чтобы
 * индикатор записи в браузере не горел за пределами разговора.
 */
let liveMicStream: MediaStream | null = null;

function micStreamIsLive(stream: MediaStream | null): stream is MediaStream {
  return !!stream && stream.getAudioTracks().some((track) => track.readyState === 'live');
}

/** Инвалидирует in-flight getUserMedia при release/теardown (гонка acquire). */
let micAcquireGen = 0;

/** Готовый поток без повторного getUserMedia; null — доступ не выдан.
 * echoCancellation/noiseSuppression — защита голосового VAD (см. ниже) от
 * самосрабатывания на голос аватара, доносящийся из колонок. */
async function acquireMicStream(): Promise<MediaStream | null> {
  if (micStreamIsLive(liveMicStream)) return liveMicStream;
  const gen = ++micAcquireGen;
  try {
    // autoGainControl обязателен для мобильных: без АРУ тихий встроенный
    // микрофон отдаёт поток на уровне, который не пробивает ни детектор речи,
    // ни само распознавание.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
    if (gen !== micAcquireGen) {
      stream.getTracks().forEach((track) => track.stop());
      return micStreamIsLive(liveMicStream) ? liveMicStream : null;
    }
    liveMicStream = stream;
    return liveMicStream;
  } catch {
    if (gen === micAcquireGen) liveMicStream = null;
    return null;
  }
}

/** Отпускает микрофон — гасит индикатор записи в браузере. */
function releaseMicStream(): void {
  micAcquireGen += 1;
  if (!liveMicStream) return;
  liveMicStream.getTracks().forEach((track) => track.stop());
  liveMicStream = null;
}

/**
 * Прогрев микрофона: платим за getUserMedia в момент запуска сессии Диалога,
 * когда пользователь ещё не говорит, а не на первом клике по кнопке записи.
 * Отказ в доступе здесь молчаливый — сообщение покажет сам клик.
 */
async function warmUpDialogMic(): Promise<void> {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;
  await acquireMicStream();
}

/** Клик по микрофону в серверном режиме: старт записи / стоп с отправкой. */
async function toggleServerSttRecording(): Promise<void> {
  if (liveRecorder) {
    stopServerSttRecording();
    return;
  }
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    showToast('Голосовой ввод недоступен в этом браузере', 'error');
    return;
  }

  setMicState('preparing');
  const stream = await acquireMicStream();
  if (!stream) {
    setMicState('idle');
    // Отказ/закрытый запрос — перевзвод останавливаем, иначе следующий цикл
    // покажет системный диалог снова (мобильный Chrome спрашивает каждый раз).
    blockMicUntilUserGesture('Нет доступа к микрофону. Разрешите доступ и включите Диалог заново.');
    return;
  }

  // Контейнер выбирается по тому, что браузер РЕАЛЬНО умеет. Раньше здесь
  // стоял безусловный webm — на iOS (Safari, и любой браузер на iPhone: все они
  // на WebKit) webm не поддерживается вовсе, конструктор бросал NotSupportedError,
  // и серверное распознавание на телефоне не работало никогда. Сервер контейнер
  // определяет по содержимому (ffmpeg -i), поэтому mp4/aac ему подходит так же,
  // как webm/opus.
  const recorder = createSttRecorder(stream);
  if (!recorder) {
    setMicState('idle');
    showToast('Голосовой ввод недоступен в этом браузере', 'error');
    return;
  }
  const mime = recorder.mimeType || 'audio/webm';
  voiceLog('serverStt: запись начата', { mime });
  const chunks: Blob[] = [];
  const meta: SttRecordingMeta = { watching: false, hadSpeech: false };
  liveRecorder = recorder;
  liveRecordingMeta = meta;

  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  recorder.onstop = () => {
    // Треки НЕ глушим: поток переиспользуется следующей фразой, иначе каждый
    // клик снова платит за getUserMedia и съедает начало реплики.
    liveRecorder = null;
    if (liveRecordingMeta === meta) liveRecordingMeta = null;
    setMicState('idle');
    // Детектор работал, но речи не было ни мгновения — это «окно тишины»
    // Свободного диалога (пользователь думает). SpeechKit такую дорожку в
    // текст не превратит — не отправляем и не платим: прод 03–04.08 часами
    // слал 30-секундную тишину в облако каждые полминуты.
    // Порог речи не пробит — но выбрасывать дорожку можно, только если в ней
    // ВООБЩЕ ничего не происходило. Пик заметно выше шумового пола означает,
    // что человек говорил, просто тихо (мобильные микрофоны после подавления
    // шума и АРУ): такую запись отдаём распознаванию, а не теряем молча.
    voiceLog('serverStt: запись остановлена', {
      watching: meta.watching,
      hadSpeech: meta.hadSpeech,
      peakRms: Number((meta.peakRms || 0).toFixed(4)),
      noiseFloor: Number((meta.noiseFloor || 0).toFixed(4)),
      chunks: chunks.length,
      abandonedByTurn: !!meta.abandonedByTurn,
    });
    // Запись из грейс-окна авто-отправки, в которой человек так и не заговорил
    // (речь сняла бы саму авто-отправку, см. VAD). Отправлять её нельзя: это
    // лишний вызов STT на каждый ход, а распознанный в ней шорох приходит уже
    // ПОСЛЕ ухода вопроса и садится началом следующего — ровно так ломалась
    // логика вопрос-ответ (прод, Яндекс.Браузер на Android, 06.08).
    if (meta.abandonedByTurn && !meta.hadSpeech) {
      voiceLog('serverStt: дорожка грейс-окна отброшена (ход уже ушёл)');
      return;
    }
    if (meta.watching && !meta.hadSpeech && !recordingHasSignal(meta)) {
      voiceLog('serverStt: дорожка отброшена как тишина');
      reportVoiceTrace('recording-discarded-silence');
      notifyNoSpeech('Не расслышал вопрос. Повторите.');
      return;
    }
    void submitServerSttAudio(new Blob(chunks, { type: mime }), meta);
  };

  recorder.start();
  setMicState('listening');
  startSilenceWatch(stream, meta, () => stopServerSttRecording());
}

/**
 * В записи есть хоть какой-то сигнал сверх собственного шума микрофона:
 * пик поднялся заметно (в 1.8 раза) над калиброванным полом. Второе условие —
 * абсолютное: на совсем глухом входе пол околонулевой, и любой шорох даёт
 * кратный «пик», за которым нет речи.
 */
function recordingHasSignal(meta: SttRecordingMeta): boolean {
  const peak = meta.peakRms || 0;
  const floor = meta.noiseFloor || 0;
  return peak > floor * 1.8 && peak > STT_VAD_MIN_RMS;
}

/**
 * MediaRecorder в контейнере, который умеет ЭТОТ браузер. Порядок — от лучшего
 * для распознавания к запасному: opus (webm/ogg) → mp4/aac (единственное, что
 * умеет WebKit на iPhone) → дефолт браузера без явного mimeType.
 */
function createSttRecorder(stream: MediaStream): MediaRecorder | null {
  const candidates = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4;codecs=mp4a.40.2',
    'audio/mp4',
  ];
  for (const mimeType of candidates) {
    if (!MediaRecorder.isTypeSupported?.(mimeType)) continue;
    try {
      return new MediaRecorder(stream, { mimeType });
    } catch {
      /* браузер соврал в isTypeSupported — пробуем следующий */
    }
  }
  // Ни один контейнер явно не подошёл: пусть браузер выберет сам.
  try {
    return new MediaRecorder(stream);
  } catch {
    return null;
  }
}

function stopServerSttRecording(): void {
  if (silenceWatchCleanup) silenceWatchCleanup();
  if (liveRecorder && liveRecorder.state !== 'inactive') {
    try {
      liveRecorder.stop();
    } catch {
      /* уже остановлен */
    }
  }
}

/**
 * Пауза, после которой фраза считается законченной и запись уходит в STT.
 *
 * СКЛЕЙКА ФРАЗЫ ДЕЛАЕТСЯ ЗДЕСЬ, а не после распознавания. Прежние 1.8 с
 * резали речь по паузам внутри мысли, и в облако уходили ОТДЕЛЬНЫЕ СЛОВА:
 * прогон 2026-08-10 вернул «Глубина», «Хищника», «Хранитель», «Мир» — и
 * «Глупино» вместо «Глубина», потому что у распознавателя не было контекста
 * фразы. Плюс каждый обрывок копился в поле ввода, и человек видел, как
 * вопросы «накапливаются».
 *
 * Склеить уже записанные куски нельзя: каждый — самостоятельный контейнер
 * webm/opus со своим заголовком, их конкатенация даёт битый файл. Поэтому
 * фраза не разрезается вовсе — пауза внутри мысли переживается ОДНОЙ записью,
 * и распознавание получает её целиком.
 *
 * Цена — примерно лишняя секунда ожидания на ход; она окупается тем, что
 * ответ приходит на фразу, а не на случайное слово из неё.
 */
const STT_SILENCE_STOP_MS = 2800;

/** Медиана — устойчивее среднего к одиночному транзиенту (клик мыши, скрип
 * стула), который иначе завысил бы шумовой пол на всю запись. */
function medianOf(values: number[]): number {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Автостоп записи: ~1.8с тишины после начала речи (RMS через WebAudio) либо
 * жёсткий кап SERVER_STT_MAX_MS. Без WebAudio остаются ручной стоп и кап.
 *
 * Порог «началась речь» калибруется по шумовому полу первых полсекунды — тем
 * же приёмом, что VAD перебивания (voiceVadTick): прежний фиксированный 0.02
 * тихий микрофон не пробивал, «начало речи» не фиксировалось, автостоп не
 * срабатывал — и каждая запись доживала до капа (прод: ровная 30-секундная
 * гребёнка пустых распознаваний). Пол на уровне речи (запись стартовала
 * поверх фразы — барж-ин) — порогом не считается: речь идёт прямо сейчас.
 *
 * Калиброванный порог ОГРАНИЧЕН СВЕРХУ прежним абсолютным (STT_SPEECH_ABS_RMS):
 * шумный вход (пол 0.013–0.019) поднял бы порог до 0.02–0.032, то есть выше
 * старого, и негромкая речь перестала бы засчитываться — а теперь такая запись
 * не просто не останавливается по тишине, но и вовсе не отправляется. Потолок
 * держит инвариант «чувствительность никогда не хуже прежней»: калибровка
 * только опускает порог для тихих микрофонов.
 *
 * Этап 2 (задел): заменить RMS-детекцию на silero-vad (ONNX в браузере).
 */
function startSilenceWatch(stream: MediaStream, meta: SttRecordingMeta, onSilence: () => void): void {
  /** Речь шла прямо в момент последнего замера — нужно капу (см. ниже). */
  let speakingNow = false;
  // Кап — единственный стоп, который может застать фразу на полуслове: стоп по
  // тишине по определению приходит после её конца, ручной жмёт сам говорящий.
  const maxTimer = setTimeout(() => {
    meta.cappedMidSpeech = speakingNow;
    onSilence();
  }, SERVER_STT_MAX_MS);
  let audioCtx: AudioContext | null = null;
  let interval: ReturnType<typeof setInterval> | null = null;
  try {
    audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(stream);
    const analyser = audioCtx.createAnalyser();
    analyser.fftSize = 2048;
    source.connect(analyser);
    const buf = new Uint8Array(analyser.fftSize);
    meta.watching = true;
    const noise: number[] = [];
    let threshold: number | null = null;
    let elapsedMs = 0;
    let silentMs = 0;
    // Шаг VAD-монитора: за полсекунды калибровки набирается пять замеров, а не
    // два — по двум точкам медиана шумового пола ничего не значит.
    interval = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i += 1) {
        const v = (buf[i] - 128) / 128;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / buf.length);
      elapsedMs += VAD_POLL_MS;
      if (elapsedMs <= VAD_CALIBRATION_MS) {
        noise.push(rms);
        return;
      }
      if (rms > (meta.peakRms || 0)) meta.peakRms = rms;
      if (threshold === null) {
        const floor = medianOf(noise);
        meta.noiseFloor = floor;
        if (floor >= STT_SPEECH_ABS_RMS) {
          // Калибровка попала на уже идущую речь: пользователь говорил с
          // первого мгновения записи. Порог — минимальный, речь уже есть.
          meta.hadSpeech = true;
          threshold = STT_VAD_MIN_RMS;
        } else {
          threshold = Math.min(
            Math.max(
              floor * STT_VAD_MULTIPLIER,
              floor + STT_VAD_MARGIN,
              STT_VAD_MIN_RMS,
              // Опыт этого устройства: уровни, на которых распознавание не
              // нашло слов, речью не считаем (см. learnNoiseFrom).
              learnedNoiseFloor,
            ),
            STT_SPEECH_ABS_RMS
          );
        }
      }
      speakingNow = rms > threshold;
      if (speakingNow) {
        meta.hadSpeech = true;
        silentMs = 0;
        markSpeechActivity();
        // Пользователь продолжил говорить в перевзведённую запись — грейс-окно
        // авто-отправки прошлой фразы снимается: реплика ещё не досказана.
        cancelFreeAutoSend();
      } else if (meta.hadSpeech) {
        silentMs += VAD_POLL_MS;
        if (silentMs >= STT_SILENCE_STOP_MS) onSilence();
      }
    }, VAD_POLL_MS);
  } catch {
    /* нет WebAudio — остаются ручной стоп и кап; meta.watching остаётся false */
  }
  silenceWatchCleanup = () => {
    clearTimeout(maxTimer);
    if (interval) clearInterval(interval);
    if (audioCtx) void audioCtx.close().catch(() => undefined);
    silenceWatchCleanup = null;
  };
}

async function submitServerSttAudio(blob: Blob, meta: SttRecordingMeta): Promise<void> {
  if (!blob.size) return;
  try {
    const audioB64 = await blobToBase64(blob);
    const r = await fetch('/api/avg-dialog/stt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + state.authToken },
      // mime отдаём фактический: на iPhone это mp4/aac, а не webm/opus.
      body: JSON.stringify({
        session_id: getSessionId(),
        audio_base64: audioB64,
        mime: blob.type || undefined,
        // Временное (этап отладки): трейс уезжает вместе с записью — на
        // телефоне консоли под рукой нет, а в журнале гейтвея он виден.
        client_log: voiceDebugEnabled() ? `ua=${navigator.userAgent}
${voiceLogTail()}` : undefined,
      }),
    });
    if (!r.ok) throw new Error(String(r.status));
    const data = (await r.json()) as { text?: string; provider?: string; latency_ms?: number };
    const text = (data.text || '').trim();
    voiceLog('serverStt: ответ', {
      provider: data.provider,
      chars: text.length,
      latency_ms: data.latency_ms,
      bytes: blob.size,
    });
    if (!text) {
      // Ни слова там, где детектор слышал речь — значит это был шум, а не
      // человек: порог этого устройства поднимается (см. learnNoiseFrom).
      learnNoiseFrom(meta);
      if (meta.hadSpeech) {
        // Речь была, но провайдер её не распознал — говорим сразу, а не через
        // 5-минутный троттлинг: молчаливый провал распознавания и был
        // «зависанием» Тренера с точки зрения пользователя.
        showToast('Не расслышал вопрос. Повторите.', 'info');
      } else {
        // Детектор не работал (нет WebAudio) — было это тишиной или речью,
        // неизвестно; остаёмся на троттлинге, чтобы не пилить думающего
        // человека упрёками каждые полминуты.
        notifyNoSpeech('Не расслышал вопрос. Повторите.');
      }
      return;
    }
    // Речь распознана — этот уровень громкости обязан проходить и впредь:
    // выученный шум не имеет права отрезать тихий голос (см. learnSpeechFrom).
    learnSpeechFrom(meta);
    if (meta.cappedMidSpeech) {
      // Кап оборвал фразу на полуслове: в Свободном диалоге текст остаётся в
      // поле — пользователь договорит его голосом (следующая запись допишет
      // хвост) или отправит сам, когда фраза станет целой.
      putTranscriptIntoInput(text, { keepInInput: true });
      showToast('Запись длиннее лимита — проверьте текст и отправьте.', 'info');
      return;
    }
    putTranscriptIntoInput(text);
  } catch {
    showToast('Распознавание недоступно. Попробуйте ещё раз или введите текст.', 'error');
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || '');
      resolve(dataUrl.slice(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/** Барж-ин разрешён только когда аватар реально говорит И настройка (профиль >
 * глобальная > env, эффективное значение уже в config.barge_in_enabled)
 * включена — раньше флаг клиента игнорировал настройку вовсе. */
function bargeInAllowed(): boolean {
  return isAvatarSpeaking() && !!config?.barge_in_enabled;
}

/** «Стоп» — глушит локальный плейбек сразу, обрывает СВОЙ же ещё не
 * завершившийся SSE-фетч (если ход ещё в HTTP) и (best-effort) просит сервер
 * прервать рендер — иначе GPU дорисовывает клип в пустоту (см. task 3,
 * wav2lip-live-avatar.adapter interrupt()). Работает и пока идёт HTTP
 * предыдущего хода: isSending не блокирует эту кнопку, только sendQuestion.
 * Нажатый до первого звука ответа — отменяет ход целиком и возвращает вопрос
 * в поле ввода (rollbackPendingQuestion), чтобы его можно было переформулировать. */
function interruptAvgDialog(): void {
  rollbackPendingQuestion();
  flushTurnTextOnInterrupt(activeTurnCtx);
  stopLivePlayback();
  currentStreamAbort?.abort();
  void sendAvgDialogInterrupt();
}

/**
 * «Стоп» до первого звука ответа: ход отменяется целиком — вопрос уходит
 * обратно в поле ввода (его для того и останавливают: переформулировать или
 * поправить), а его бабл и «Думаю…» снимаются. Ход не считается репликой и на
 * сервере (см. abandonAvgDialogTurn) — история модели о нём не узнаёт.
 * Ответ уже пошёл — no-op: прерывание работает по-старому.
 */
function rollbackPendingQuestion(): void {
  const question = pendingQuestionText;
  if (!question) return;
  pendingQuestionText = null;

  const last = messages[messages.length - 1];
  if (last && last.role === 'user' && last.text === question) messages.pop();
  renderMessages();

  const input = $('adlg-input') as HTMLTextAreaElement | null;
  if (!input) return;
  // Пользователь мог начать набирать следующий вопрос, пока ждал ответа — его
  // текст важнее возвращаемого.
  if (!input.value.trim()) input.value = question;
  input.focus();
  autoResizeDialogInput();
}

async function sendAvgDialogInterrupt(): Promise<void> {
  try {
    await fetch('/api/avg-dialog/interrupt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + state.authToken },
      body: JSON.stringify({ session_id: getSessionId() }),
    });
  } catch {
    // best-effort — локальный стоп уже произошёл, серверный barge-in опционален
  }
}

// ---- Голосовое перебивание (VAD поверх прогретого микрофонного потока) ----
// Включаемая настройка (config.voice_barge_in_enabled, дефолт выключено),
// эффективна только вместе с обычным barge-in. Работает поверх УЖЕ открытого
// liveMicStream (см. warmUpDialogMic выше) — сам доступ к микрофону не
// запрашивает: нет потока — монитор просто не анализирует звук.
const VAD_POLL_MS = 100;
/** Калибровка шумового пола в начале хода — заодно защита от самосрабатывания
 * на первый момент голоса аватара из колонок (echoCancellation режет основное,
 * это добивает остаточное эхо/наводку). */
const VAD_CALIBRATION_MS = 500;
/** Непрерывное превышение порога такой длительности считается началом речи
 * пользователя, а не случайным шумом/покашливанием. */
const VAD_SUSTAIN_MS = 300;
const VAD_RMS_MULTIPLIER = 1.6;
const VAD_RMS_MARGIN = 0.01;
const VAD_MIN_RMS = 0.015;
const VAD_FFT_SIZE = 2048;

let vadPollTimer: ReturnType<typeof setInterval> | null = null;
let vadAudioCtx: AudioContext | null = null;
let vadAnalyser: AnalyserNode | null = null;
let vadSourceStream: MediaStream | null = null;
const vadBuf = new Uint8Array(VAD_FFT_SIZE);
let vadSpeakingPrev = false;
let vadSpeakingSince = 0;
let vadNoiseSamples: number[] = [];
let vadThreshold: number | null = null;
let vadAboveMs = 0;
/** Гистерезис: после срабатывания монитор молчит, пока не завершится СЛЕДУЮЩИЙ
 * ход целиком (не текущий, оборванный interruptAvgDialog) — иначе собственный
 * же стоп-переход isAvatarSpeaking() true->false немедленно снял бы гистерезис. */
let vadDisarmed = false;
let vadSawTurnStartSinceTrigger = false;

function resetVoiceVadState(): void {
  vadSpeakingPrev = false;
  vadSpeakingSince = 0;
  vadNoiseSamples = [];
  vadThreshold = null;
  vadAboveMs = 0;
  vadDisarmed = false;
  vadSawTurnStartSinceTrigger = false;
}

/** (Пере)подключает AnalyserNode к текущему потоку. Не трогает getUserMedia —
 * вызывается только когда liveMicStream уже прогрет. */
function ensureVoiceVadAnalyser(stream: MediaStream): AnalyserNode | null {
  if (vadAnalyser && vadSourceStream === stream) return vadAnalyser;
  try {
    if (!vadAudioCtx) vadAudioCtx = new AudioContext();
    const source = vadAudioCtx.createMediaStreamSource(stream);
    const analyser = vadAudioCtx.createAnalyser();
    analyser.fftSize = VAD_FFT_SIZE;
    source.connect(analyser);
    vadAnalyser = analyser;
    vadSourceStream = stream;
    return analyser;
  } catch {
    return null;
  }
}

function readVoiceRms(analyser: AnalyserNode): number {
  analyser.getByteTimeDomainData(vadBuf);
  let sum = 0;
  for (let i = 0; i < vadBuf.length; i += 1) {
    const v = (vadBuf[i] - 128) / 128;
    sum += v * v;
  }
  return Math.sqrt(sum / vadBuf.length);
}

function triggerVoiceBargeIn(): void {
  vadDisarmed = true;
  vadSawTurnStartSinceTrigger = false;
  vadAboveMs = 0;
  interruptAvgDialog();
  // Фраза пользователя, которую уже поймал VAD, сразу становится вводом —
  // тем же STT-путём, что и ручной клик по микрофону.
  startLiveVoiceInput();
}

function voiceVadTick(): void {
  const allowed = isLiveMode() && !!config?.voice_barge_in_enabled
    && !!config?.barge_in_enabled && !micMuted;
  const speaking = isAvatarSpeaking();

  // Начало и конец озвучки — единственный сигнал о том, что аватар заговорил
  // или замолчал (событий у очереди воспроизведения нет), поэтому «Стоп»
  // подменяет главное действие именно здесь.
  if (speaking !== vadSpeakingPrev) updatePrimaryAction();

  // Границы хода отслеживаем независимо от `allowed` — переключение настройки
  // администратором на лету не должно ломать гистерезис/калибровку.
  if (speaking && !vadSpeakingPrev) {
    vadSpeakingSince = Date.now();
    vadNoiseSamples = [];
    vadThreshold = null;
    vadAboveMs = 0;
    if (vadDisarmed) vadSawTurnStartSinceTrigger = true;
  } else if (!speaking && vadSpeakingPrev) {
    if (vadDisarmed && vadSawTurnStartSinceTrigger) {
      vadDisarmed = false;
      vadSawTurnStartSinceTrigger = false;
    }
  }
  vadSpeakingPrev = speaking;

  // Свободный диалог: микрофон включается сам, как только аватар договорил
  // (или ход завершился без озвучки). Опрос, а не событие — так режим
  // самовосстанавливается после любой ошибки распознавания.
  maybeRearmFreeDialog();

  if (!allowed || !speaking || vadDisarmed) return;
  // Доступа к микрофону нет (или ещё не прогрет) — сами getUserMedia не зовём.
  if (!micStreamIsLive(liveMicStream)) return;

  const analyser = ensureVoiceVadAnalyser(liveMicStream);
  if (!analyser) return;

  const rms = readVoiceRms(analyser);
  const elapsedSinceSpeaking = Date.now() - vadSpeakingSince;
  if (elapsedSinceSpeaking < VAD_CALIBRATION_MS) {
    vadNoiseSamples.push(rms);
    return;
  }
  if (vadThreshold === null) {
    const floor = vadNoiseSamples.length
      ? vadNoiseSamples.reduce((a, b) => a + b, 0) / vadNoiseSamples.length
      : 0;
    vadThreshold = Math.max(floor * VAD_RMS_MULTIPLIER, floor + VAD_RMS_MARGIN, VAD_MIN_RMS);
  }

  if (rms > vadThreshold) {
    vadAboveMs += VAD_POLL_MS;
    if (vadAboveMs >= VAD_SUSTAIN_MS) triggerVoiceBargeIn();
  } else {
    vadAboveMs = 0;
  }
}

/** Запускает поллинг на время live-сессии. Тик ведёт три вещи: подмену
 * главного действия на «Стоп», перевзвод Свободного диалога и — только когда
 * настройка включена — сам VAD (анализ громкости идёт под `allowed` внутри
 * тика, так что выключенное перебивание ресурсов не ест).
 * Идемпотентен: сначала гасит предыдущий таймер (смена профиля/рестарт). */
function maybeStartVoiceVadMonitor(): void {
  stopVoiceVadMonitor();
  if (!isLiveMode()) return;
  resetVoiceVadState();
  vadPollTimer = setInterval(voiceVadTick, VAD_POLL_MS);
}

function stopVoiceVadMonitor(): void {
  if (vadPollTimer) {
    clearInterval(vadPollTimer);
    vadPollTimer = null;
  }
  if (vadAudioCtx) {
    void vadAudioCtx.close().catch(() => undefined);
    vadAudioCtx = null;
  }
  vadAnalyser = null;
  vadSourceStream = null;
}

function bindEvents(): void {
  if (eventsBound) return;
  eventsBound = true;

  $('adlg-send-btn')?.addEventListener('click', () => {
    // Отправка руками — тоже жест: право на звук берём и здесь, иначе голос
    // ответа пропадёт у того, кто спрашивает текстом.
    primePlaybackWithGesture();
    const input = $('adlg-input') as HTMLTextAreaElement | null;
    const text = input?.value || '';
    // При включённом голосовом вводе стрелка не отправляет мгновенно, а
    // заканчивает набор: она пропадает, микрофон снова слушает (кнопка
    // Диалога пульсирует) и работает обычное грейс-окно — молчание 5 с
    // отправляет текст аватару, а речь дописывается в него и окно
    // перезапускается. Так набранное руками можно дополнить голосом.
    if (freeDialogEnabled && text.trim()) {
      endTextEditing();
      if (micMuted) setMicMuted(false);
      scheduleFreeAutoSend();
      return;
    }
    void sendQuestion(text, { bargeIn: bargeInAllowed() });
  });

  // Одна и та же кнопка в двух местах: в строке ввода (экран 2) и поверх
  // кадра (экран 1) — состояние у них общее, обработчик тоже.
  const onFreeDialogToggle = (): void => {
    // Жест — законный повод снова просить доступ к микрофону.
    allowMicRequestsAgain();
    // И единственный момент, когда можно получить право на звук: ответ придёт
    // через секунды, разрешение к тому времени «остынет» (см.
    // primePlaybackWithGesture).
    primePlaybackWithGesture();
    // Проба ещё идёт: включать режим вхолостую нечестно — человек начнёт
    // говорить в неготовый микрофон. Запоминаем намерение и говорим об этом;
    // режим включится сам, как только голос будет готов.
    if (!freeDialogEnabled && voiceReadiness === 'probing') {
      freeDialogWantedWhenReady = true;
      showToast('Голос ещё готовится — включу, как будет готов');
      return;
    }
    if (voiceReadiness === 'blocked') void initVoiceReadiness();
    setFreeDialogEnabled(!freeDialogEnabled);
  };
  $('adlg-free-btn')?.addEventListener('click', onFreeDialogToggle);
  $('adlg-stage-free')?.addEventListener('click', onFreeDialogToggle);

  $('adlg-mic-toggle')?.addEventListener('click', () => {
    // Ручной клик по тумблеру отменяет память авто-паузы экрана: дальше
    // состоянием микрофона распоряжается пользователь, а не переходы.
    screenAutoMuted = false;
    allowMicRequestsAgain();
    setMicMuted(!micMuted);
  });

  // Портретный мобильный: кнопки-дубли жеста перелистывания экранов. Паузу
  // микрофона они переключают сами, не дожидаясь наблюдателя, — свайп
  // докрывает IntersectionObserver из setupPortraitScreens.
  $('adlg-jump-chat')?.addEventListener('click', () => {
    autoMuteForChatScreen();
    document.querySelector('#view-avg-dialog .adlg-panel-controls')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  $('adlg-jump-video')?.addEventListener('click', () => {
    restoreMicAfterChatScreen();
    document.querySelector('#view-avg-dialog .adlg-avatar-stage')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  setupPortraitScreens();
  setupStageColumnSync();

  $('adlg-stop-btn')?.addEventListener('click', () => {
    interruptAvgDialog();
    updatePrimaryAction();
  });

  // Ручная правка поля — режим правки: запись глушится, её тумблер и кнопка
  // Диалога уходят, остаётся «Отправить» (beginTextEditing).
  $('adlg-input')?.addEventListener('input', () => {
    autoResizeDialogInput();
    // Ручная правка во время диктовки — неявная пауза (тот же тумблер
    // микрофона): иначе следующий interim-кадр затёр бы правку, а грейс-таймер
    // отправил бы недоправленное. Событие input стреляет только на действия
    // пользователя — программная запись value его не поднимает.
    beginTextEditing();
    updatePrimaryAction();
  });

  // Курсор поставлен в поле — человек собрался править, даже если ещё не
  // набрал ни символа: стрелка «Отправить» должна быть под рукой сразу, а не
  // после первой буквы. Программная фокусировка (надиктованный текст на паузе)
  // за правку не считается — см. suppressFocusEditing.
  $('adlg-input')?.addEventListener('focus', () => {
    if (suppressFocusEditing) return;
    beginTextEditing();
    updatePrimaryAction();
  });

  $('adlg-input')?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const input = $('adlg-input') as HTMLTextAreaElement | null;
      void sendQuestion(input?.value || '', { bargeIn: bargeInAllowed() });
    }
  });

  $('adlg-save-chat')?.addEventListener('click', () => {
    void saveChatToConversation();
  });

  $('adlg-new-dialog')?.addEventListener('click', () => {
    // Новый диалог стирает беседу безвозвратно (сессия и транскрипт на сервере
    // закрываются), а кнопка сохранения стоит прямо под этой — переспрашиваем.
    if (hasUnsavedTurns()
      && !confirm('Текущий чат не сохранён в Разговор. Начать новый диалог и потерять его?')) {
      return;
    }
    void (async () => {
      await restartDialog();
      showToast('Новый диалог начат');
    })();
  });

  $('adlg-avatar-profile')?.addEventListener('change', () => void onAvatarProfileChange());
}

export async function loadAvgDialogView(): Promise<void> {
  bindEvents();

  try {
    const r = await fetch('/api/avg-dialog/config', {
      headers: { Authorization: 'Bearer ' + state.authToken },
    });
    if (!r.ok) throw new Error('Не удалось загрузить конфигурацию');

    config = await r.json() as AvgDialogConfig;
    maxTurns = config.max_turns || 30;

    if (!config.enabled || !config.categories?.length) {
      const root = $('adlg-root');
      if (root) {
        root.innerHTML = '<div class="adlg-empty">Диалог временно недоступен.</div>';
      }
      return;
    }

    // The avatar is assigned per-user by an admin (dialog_avatar_id). Without a
    // valid profile the dialog can't run — show the error instead of an avatar.
    if (config.avatar_error) {
      const root = $('adlg-root');
      if (root) {
        root.innerHTML = `<div class="adlg-empty">🚫 ${escapeHtml(config.avatar_error)}</div>`;
      }
      return;
    }

    // Category is derived from the user's own category (no in-dialog switcher).
    activeCategory = state.currentUser?.category
      && config.categories.some((c) => c.name === state.currentUser?.category)
      ? state.currentUser.category
      : config.categories[0].name;

    updateAvatarPanel();
    renderMessages();
    updateTurnHint();
    await renderAvatarProfileSelector();

    if (isLiveMode()) {
      await startLiveAvatarSession();
    } else {
      setAvatarVideo(idleVideoUrl());
    }
  } catch (err) {
    const root = $('adlg-root');
    if (root) {
      root.innerHTML = '<div class="adlg-empty">Не удалось загрузить Диалог.</div>';
    }
    showToast('❌ ' + (err instanceof Error ? err.message : String(err)));
  }
}

export function initAvgDialog(): void {
  bindEvents();
}

export async function teardownAvgDialog(): Promise<void> {
  // Уход с вкладки гасит режим целиком: иначе микрофон переподнимался бы
  // тиком, пока живёт сессия; незакрытый SSE продолжал бы озвучку поверх
  // другого раздела.
  freeDialogEnabled = false;
  cancelFreeAutoSend();
  stopFreeIdleWatch();
  micMuted = false;
  typedByHand = false;
  screenAutoMuted = false;
  stopListening();
  releaseMicStream();
  stopLivePlayback();
  currentStreamAbort?.abort();
  currentStreamAbort = null;
  await endLiveAvatarSession();
}
